// app/spraytest/page.js
// Главная страница тестирования спрей-движка
// Версия: 1.15.10

'use client';

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useParams, useRouter, usePathname } from 'next/navigation';
import initSprayEngine from './sprayEngine.client';
import { presets, defaultCans, calculateDynamicParams } from './sprayPresets';
import { usePresetManager } from './usePresetManager';
import { useSprayControls } from './useSprayControls';
import { useCanManager } from './useCanManager';
import { usePaintBridge } from './usePaintBridge';
import { usePreviewFade } from './usePreviewFade';
import { useTheme } from '../../hooks/useTheme';
import CanvasMinimap from '../../ds/components/CanvasMinimap';
import { changelogData } from './changelogData';
import { getTranslation } from './i18n';
import Link from 'next/link';
import Button from '../../ds/components/Button';
import Switcher from '../../ds/components/Switcher';
import RangeSlider from '../../ds/components/RangeSlider';
import Tab from '../../ds/components/Tab';
import ColorIndicator from '../../ds/components/ColorIndicator';
import FloatingToolbar from '../../ds/components/FloatingToolbar';
import ColorTile from '../../ds/components/ColorTile';
import Input from '../../ds/components/Input';
import Dropdown from '../../ds/components/Dropdown';
import Chip from '../../ds/components/Chip';
import PanelToggle from '../../ds/components/PanelToggle';
import './spraytest.css';
import montanaColors from './data/montanaColors.json';
import { useCanvasPersistence } from './useCanvasPersistence';
import { CanPickerItem, CanPickerCanvas } from '../../components/CanPicker3D';
import dynamic from 'next/dynamic';
// Three.js / R3F использует WebGL и браузерные API — нельзя рендерить на сервере.
// dynamic + ssr:false гарантирует, что компонент загружается только на клиенте.
const ClientWall3D = dynamic(() => import('../3dWall/ClientWall3D'), { ssr: false, loading: () => null });

// ---------------------------------------------------------------------------
// localStorage helpers — safe wrappers around browser storage
// ---------------------------------------------------------------------------
const lsGet = (key, fallback) => {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (raw === null || raw === undefined) return fallback;
    return JSON.parse(raw);
  } catch { return fallback; }
};
const lsSet = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* noop */ }
};
const lsDel = (key) => {
  try { localStorage.removeItem(key); } catch { /* noop */ }
};

// localStorage keys for spraytest state
const LS = {
  CANS: 'sw:spray_cans',
  COLOR: 'sw:spray_color',
  ACTIVE_CAN: 'sw:spray_active_can',
  PRESET: 'sw:spray_preset',
  OVERRIDES: 'sw:spray_overrides',
  FLAT_MODE: 'sw:spray_flat_mode',
  SLIDER_VALS: 'sw:spray_sliders', // direct slider values {radius, density, scale, speedFactor, preset}
  ZOOM: 'sw:spray_zoom',
  VIEWPORT: 'sw:spray_viewport', // { left, top }
  ONLY_ZOOM: 'sw:spray_only_zoom',
  SHOW_GRID: 'sw:spray_show_grid',
  GHOST_ENABLED: 'sw:spray_ghost_enabled',
  PAN_MODE: 'sw:spray_pan_mode',
  FULLSCREEN: 'sw:spray_fullscreen',
  LAST_SPRAY: 'sw:active_spray_can',
  LAST_ROLLER: 'sw:active_roller_can',
};

export default function SprayTestPage() {
  // ============================================================================
  // СОСТОЯНИЕ КОМПОНЕНТА
  // ============================================================================

  const currentLanguages = ['en', 'ru', 'es', 'pt', 'de', 'fr', 'th', 'ko', 'zh', 'ja', 'hi'];
  const langLabels = {
    ru: 'CYR',
    zh: '中文',
    ja: '日本',
    ko: '한국',
    th: 'ไทย',
    hi: 'हिन्दी'
  };

  // Параметры управления
  const [scaleVal, setScaleVal] = useState(0.65);      // Line Scale
  const [radiusVal, setRadiusVal] = useState(45);      // Радиус
  const [densityVal, setDensityVal] = useState(5600);  // Плотность
  const [speedFactorVal, setSpeedFactorVal] = useState(1.5); // Скорость

  // Актуальные параметры для движка
  const [actualRadius, setActualRadius] = useState(45);
  const [actualDensity, setActualDensity] = useState(5600);
  const [actualSpeed, setActualSpeed] = useState(0.65);
  const [actualSpeedFactor, setActualSpeedFactor] = useState(1.5);

  // Пресеты и пользовательские настройки — SSR-safe defaults (localStorage read in useEffect below)
  const [activePreset, setActivePresetState] = useState('Fat Cap');
  const setActivePreset = (v) => { setActivePresetState(v); lsSet(LS.PRESET, v); };
  const [flatMode, setFlatModeState] = useState(false);
  const setFlatMode = (v) => { setFlatModeState(v); lsSet(LS.FLAT_MODE, v); };
  const [userOverrides, setUserOverridesState] = useState({});
  // NOTE: lsSet must be called OUTSIDE the state updater to avoid Strict Mode double-execution
  const setUserOverrides = (updater) => {
    setUserOverridesState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      // Schedule save asynchronously so we don't call side-effects inside React's pure updater
      setTimeout(() => lsSet(LS.OVERRIDES, next), 0);
      return next;
    });
  };

  const [is3DMode, setIs3DModeState] = useState(false); // (false);
  const setIs3DMode = (v) => { setIs3DModeState(v); lsSet('sw:spray_3d_mode', v); };

  // Custom Hook для управления состоянием пресетов
  const presetManager = usePresetManager(
    presets,
    activePreset,
    scaleVal,
    radiusVal,
    densityVal,
    speedFactorVal
  );

  // Система баллончиков — SSR-safe defaults (localStorage read in useEffect below)
  const [cans, setCansState] = useState(defaultCans);
  const setCans = (updater) => {
    setCansState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      lsSet(LS.CANS, next);
      return next;
    });
  };
  const [currentColor, setCurrentColorState] = useState('#ff3366');
  const setCurrentColor = (v) => { setCurrentColorState(v); lsSet(LS.COLOR, v); };
  const [activeCanId, setActiveCanIdState] = useState(5);
  const setActiveCanId = (v) => { setActiveCanIdState(v); lsSet(LS.ACTIVE_CAN, v); };


  // Выбор цвета
  const [showColorPickerFor, setShowColorPickerFor] = useState(null);
  const [tempColor, setTempColor] = useState('#ff3366');
  const [activeColorTab, setActiveColorTab] = useState('montana');
  const [colorViewMode, setColorViewMode] = useState('cans'); // 'grid' or 'cans'
  // Reset custom rows when picker opens to ensure fresh state
  useEffect(() => {
    if (showColorPickerFor) {
      setCustomColorRows([{ hex: '', name: '' }]);
    }
  }, [showColorPickerFor]);
  const [customColorRows, setCustomColorRows] = useState(
    [{ hex: '', name: '' }]
  );

  // Мобильные шторки и модальные окна
  const [showMobileToolSheet, setShowMobileToolSheet] = useState(false);
  const [showMobileSettingsModal, setShowMobileSettingsModal] = useState(false);
  const [showMobileCanvasSheet, setShowMobileCanvasSheet] = useState(false);
  const [showChangelogModal, setShowChangelogModal] = useState(false);
  const [showLangModal, setShowLangModal] = useState(false);
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [showHotkeysModal, setShowHotkeysModal] = useState(false);
  const [showClearConfirm, setShowClearConfirmState] = useState(false);
  const showClearConfirmRef = useRef(false);

  const setShowClearConfirm = useCallback((val) => {
    showClearConfirmRef.current = val;
    setShowClearConfirmState(val);
  }, []);

  const [emptyCanAlert, setEmptyCanAlert] = useState({ show: false, canId: null });

  useEffect(() => {
    window.triggerPaintEmptyAlert = (canId) => {
      setEmptyCanAlert({ show: true, canId });
    };
    return () => { window.triggerPaintEmptyAlert = null; };
  }, []);

  const handleRefill = (canId) => {
    setCans(prev => {
      const next = prev.map(c => c.id === canId ? { ...c, paintLeft: 10000 } : c);
      const updatedCan = next.find(c => c.id === canId);
      if (window.syncPaintState) window.syncPaintState(next);
      if (canId === activeCanId && window.setActiveCan) {
        window.setActiveCan(canId, updatedCan.color, next);
      }
      return next;
    });
    setEmptyCanAlert({ show: false, canId: null });
  };

  const handleRemoveCan = (canIdToDelete) => {
    setCans(prev => {
      // Separating rollers (id < 5) and sprays (id >= 5)
      const rollers = prev.filter(c => c.id < 5);
      const sprays = prev.filter(c => c.id >= 5 && c.id !== canIdToDelete);

      // Re-index remaining sprays starting from 5
      const reindexedSprays = sprays.map((can, index) => ({
        ...can,
        id: 5 + index
      }));

      const next = [...rollers, ...reindexedSprays];

      // Update activeCanId if it was deleted or its ID changed
      let finalActiveId = activeCanId;
      if (canIdToDelete === activeCanId) {
        finalActiveId = Math.max(5, Math.min(canIdToDelete, 5 + reindexedSprays.length - 1));
      } else if (activeCanId > canIdToDelete) {
        finalActiveId = activeCanId - 1;
      }

      const activeCan = next.find(c => c.id === finalActiveId) || next[next.length - 1];
      if (activeCan) {
        setActiveCanId(activeCan.id);
        setCurrentColor(activeCan.color || '#ff3366');
        if (window.setActiveCan) window.setActiveCan(activeCan.id, activeCan.color, next);
      }

      if (window.syncPaintState) window.syncPaintState(next);
      return next;
    });
    setEmptyCanAlert({ show: false, canId: null });
  };

  const [showMobileTopMenu, setShowMobileTopMenu] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [zoom3D, setZoom3D] = useState(0.8); // 3D Zoom level (distance from wall)

  useEffect(() => {
    window.onSyncSavingState = (state) => setIsSaving(state);
    return () => { delete window.onSyncSavingState; };
  }, []);

  useEffect(() => {
    window.onSyncSavingState = (state) => setIsSaving(state);
    return () => { delete window.onSyncSavingState; };
  }, []);

  // Восстановление 3D режима навигации
  useLayoutEffect(() => {
    const saved3d = lsGet('sw:spray_3d_mode', false);
    setIs3DModeState(saved3d);
  }, []);

  const closeAllModals = useCallback(() => {
    setShowMobileToolSheet(false);
    setShowMobileSettingsModal(false);
    setShowMobileCanvasSheet(false);
    setShowChangelogModal(false);
    setShowLangModal(false);
    setShowThemeModal(false);
    setShowHotkeysModal(false);
    setShowMobileTopMenu(false);
    setShowColorPickerFor(null);
    setShowClearConfirm(false);
  }, [setShowClearConfirm]);
  const [isResetting, setIsResetting] = useState(false); // --- Состояния UI ---

  useEffect(() => {
    // Lock body scroll within the prototype to avoid page bouncing
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    const handleCloseSheets = () => {
      setShowMobileToolSheet(false);
      setShowMobileSettingsModal(false);
      setShowMobileCanvasSheet(false);
    };
    window.addEventListener('closeMobileSheets', handleCloseSheets);

    // Viewport persistence — save scroll position to localStorage
    const container = document.getElementById('canvas-container');
    let scrollTimer;
    const handleScroll = () => {
      if (scrollTimer) clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        if (container) lsSet(LS.VIEWPORT, { left: container.scrollLeft, top: container.scrollTop });
      }, 400); // Debounce to prevent heavy writes
    };
    if (container) container.addEventListener('scroll', handleScroll);

    return () => {
      // Restore body scroll when leaving the prototype
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      window.removeEventListener('closeMobileSheets', handleCloseSheets);
      if (container) container.removeEventListener('scroll', handleScroll);
      if (scrollTimer) clearTimeout(scrollTimer);
    };
  }, []);
  // Preview / Test Drive mode
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewColor, setPreviewColor] = useState(null);   // color being tested
  const [multiPreviewColors, setMultiPreviewColors] = useState(null); // array of {hex, name} when testing multiple
  const [previewColorName, setPreviewColorName] = useState(null); // name of the color (if from Montana)
  const [previewCanId, setPreviewCanId] = useState(null);   // can being tested
  const [previewTargetCan, setPreviewTargetCan] = useState(null); // full can object
  const previewCanvasRef = useRef(null);
  const pendingColorNameRef = useRef(null); // set right before applySelectedColor for named colors

  // Ghost Stroke Practice (Auto Ghost Mode) — first stroke after color/preset change fades
  const [isAutoGhost, setIsAutoGhost] = useState(false);
  const autoGhostRef = useRef(false);
  const [isGhostAllowed, setIsGhostAllowed] = useState(false);
  const [isGhostEnabled, setIsGhostEnabled] = useState(() => lsGet(LS.GHOST_ENABLED, false));

  // New UX states for slider persistence and delayed button appearance
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [showResetBtn, setShowResetBtn] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const resetTimerRef = useRef(null);
  const zoomSaveTimerRef = useRef(null);
  const targetScrollRef = useRef(null);
  // Flag to skip applyPreset on mount — useLayoutEffect already restores LS values
  const isInitializedRef = useRef(false);

  const [showGrid, setShowGridState] = useState(false);
  const setShowGrid = (v) => { setShowGridState(v); lsSet(LS.SHOW_GRID, v); };
  const [isOnlyZoomMode, setIsOnlyZoomMode] = useState(false); // Режим "Только зум" для панели управления холстом
  const [zoom, setZoomState] = useState(1); // Масштаб холста (1 = 100%, 0.2 = 20%, 3 = 300%)
  const setZoom = (v) => {
    setZoomState(v);
    if (zoomSaveTimerRef.current) clearTimeout(zoomSaveTimerRef.current);
    zoomSaveTimerRef.current = setTimeout(() => lsSet(LS.ZOOM, v), 500);
  };
  const [isPanning, setIsPanning] = useState(false); // Состояние для отслеживания панорамирования
  const [hoverZoomText, setHoverZoomText] = useState(null); // Текст зума при наведении

  // Локализация из URL
  const params = useParams();
  const router = useRouter();
  const pathname = usePathname();
  const lang = params?.lang || 'en';

  // Тема
  const { themeMode, styleMode, actualTheme, changeTheme, changeStyle } = useTheme();


  // Защита от свайпа "назад/вперед" двумя пальцами (macOS gesture)
  useEffect(() => {
    const handleNavigationSwipe = (e) => {
      // Игнорируем если это вертикальный скролл или зум (у них есть свои обработчики)
      if (Math.abs(e.deltaX) < Math.abs(e.deltaY) || e.ctrlKey || e.metaKey) return;

      const container = document.getElementById('canvas-container');
      if (!container) return;

      const isAtLeft = container.scrollLeft <= 0;
      const isAtRight = container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;

      // Если мы у края и пытаемся свайпнуть дальше — блокируем нативное поведение браузера
      if ((isAtLeft && e.deltaX < 0) || (isAtRight && e.deltaX > 0)) {
        if (e.cancelable) e.preventDefault();
      }
    };

    window.addEventListener('wheel', handleNavigationSwipe, { passive: false });
    return () => window.removeEventListener('wheel', handleNavigationSwipe);
  }, []);

  // Trigger auto ghost on color/preset change
  useEffect(() => { setIsGhostAllowed(true); }, []);
  useEffect(() => {
    if (!isGhostAllowed || !isGhostEnabled) return;
    if (isPreviewMode && !autoGhostRef.current) return;

    setIsAutoGhost(true);
    autoGhostRef.current = true;
    setIsPreviewMode(true);

    // Sync preview state for FloatingToolbar and Engine
    const color = currentColor;
    const canId = activeCanId;
    setPreviewColor(color);
    setPreviewCanId(canId);
    setPreviewTargetCan(cans.find(c => c.id === canId));

    if (window.setPreviewMode && previewCanvasRef.current) {
      window.setPreviewMode(true, previewCanvasRef.current.getContext('2d'));
    }

    // Force engine update to ensure we spray the CORRECT color immediately
    if (window.updateSprayParams) {
      const selectedPreset = presets[activePreset];
      const can = cans.find(c => c.id === canId);
      const paintLeft = can ? can.paintLeft : 10000;
      const dynamicParams = calculateDynamicParams(paintLeft, activePreset);
      const overridenSliders = userOverrides[activePreset] || {};
      const finalRadius = overridenSliders.radius ?? dynamicParams.radius;
      const finalDensity = overridenSliders.density ?? dynamicParams.density;
      const finalSpeedFactor = overridenSliders.speedFactor ?? dynamicParams.speedFactor;
      const finalScale = overridenSliders.scale ?? selectedPreset.scaleVal;

      window.updateSprayParams(
        finalRadius, finalDensity, finalScale, finalSpeedFactor,
        activePreset,
        selectedPreset?.enableDrips || false,
        selectedPreset?.paintConsumption || 1,
        selectedPreset,
        canId, color, cans
      );
    }
  }, [currentColor, activePreset, isGhostAllowed]);

  // Wrap onSprayEnd to handle finishing the ghost stroke
  useEffect(() => {
    const originalOnSprayEnd = window.onSprayEnd;
    window.onSprayEnd = (dirtyArray) => {
      if (autoGhostRef.current) {
        autoGhostRef.current = false;
        if (window.setPreviewMode) window.setPreviewMode(false);
        // Delay React state cleanup to allow usePreviewFade to work
        setTimeout(() => {
          setIsAutoGhost(false);
          setIsPreviewMode(false);
        }, 800);
      }
      if (originalOnSprayEnd) originalOnSprayEnd(dirtyArray);
    };
    return () => { window.onSprayEnd = originalOnSprayEnd; };
  }, [isAutoGhost]);

  // Счётчик просмотров
  const [viewsCount, setViewsCount] = useState(null);

  useEffect(() => {
    // На проде — инкрементируем; на локалке — просто читаем текущее значение
    const method = process.env.NODE_ENV === 'production' ? 'POST' : 'GET';
    fetch('/api/views', { method })
      .then(res => res.json())
      .then(data => {
        if (data && data.count != null) {
          setViewsCount(data.count);
        }
      })
      .catch(err => console.error('Failed to update views count', err));
  }, []);

  // Состояния для развернутых панелей
  const [isControlsPanelExpanded, setIsControlsPanelExpanded] = useState(false);
  const [isControlsCanvasExpanded, setIsControlsCanvasExpanded] = useState(false);
  const [isFullscreen, setIsFullscreenState] = useState(false); // Полноэкранный режим
  const setIsFullscreen = (v) => {
    setIsFullscreenState(v);
    lsSet(LS.FULLSCREEN, v);
    // При выключении полноэкранного режима по умолчанию всегда включается режим spray
    if (!v) {
      setIsPanMode(false);
    }
  };
  // Состояние активного слайда (инструмента) для свайпера
  const [activeSlideType, setActiveSlideTypeState] = useState('spray'); // 'spray' | 'roller'
  const [lastSprayId, setLastSprayId] = useState(() => lsGet(LS.LAST_SPRAY, 5));
  const [lastRollerId, setLastRollerId] = useState(() => lsGet(LS.LAST_ROLLER, 1));
  const [isPanMode, setIsPanModeState] = useState(true); // Режим перемещения холста
  const setIsPanMode = useCallback((v) => { 
    setIsPanModeState(v); 
    lsSet(LS.PAN_MODE, v); 
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Toggle pan mode on Slash /
      if (e.code === 'Slash' || e.key === '/') {
        setIsPanMode(prev => !prev);
      }
      // Set space pressed for grab mode
      if (e.code === 'Space' && !e.repeat) {
        if (document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'TEXTAREA') {
          setIsSpacePressed(true); 
        }
      }
    };
    const handleKeyUp = (e) => {
      if (e.code === 'Space') setIsSpacePressed(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [setIsPanMode]);

  const [showEphemeralMinimap, setShowEphemeralMinimap] = useState(false); // Для временного отображения миникарты при панорамировании
  const isMinimapHoveredRef = useRef(false);
  const minimapHideTimerRef = useRef(null);

  const triggerEphemeralMinimap = useCallback(() => {
    setShowEphemeralMinimap(true);
    if (minimapHideTimerRef.current) clearTimeout(minimapHideTimerRef.current);

    minimapHideTimerRef.current = setTimeout(() => {
      if (!isMinimapHoveredRef.current) {
        setShowEphemeralMinimap(false);
      }
    }, 2000);
  }, []);

  // Отдельные state для превью цвета в табах тулбара (не сбрасываются при смене другого инструмента)
  const [toolbarSprayColor, setToolbarSprayColor] = useState('#ff3366'); // Цвет активного баллончика
  const [toolbarSprayPct, setToolbarSprayPct] = useState(100); // Остаток краски
  const [toolbarRollerColor, setToolbarRollerColor] = useState('#ffffff'); // Цвет активного валика
  const [toolbarRollerPct, setToolbarRollerPct] = useState(100); // Остаток краски валика

  // Состояние активного слайда (инструмента) для свайпера
  // const [activeSlideType, setActiveSlideType] = useState('spray'); // 'spray' | 'roller'

  // Функции для Custom Color вкладки
  const updateCustomColorRow = (index, field, value) => {
    setCustomColorRows(prev => {
      const newRows = [...prev];
      newRows[index] = { ...newRows[index], [field]: value };
      return newRows;
    });
  };

  const clearCustomColorRow = (index) => {
    setCustomColorRows(prev => {
      if (prev.length === 1) return [{ hex: '', name: '' }];
      return prev.filter((_, i) => i !== index);
    });
  };

  const addMoreCustomRows = () => {
    setCustomColorRows(prev => [...prev, { hex: '', name: '' }]);
  };

  // ============================================================================
  // REFS ДЛЯ ДОСТУПА К DOM
  // ============================================================================

  const scaleValRef = useRef(null);
  const radiusValRef = useRef(null);
  const densityValRef = useRef(null);
  const speedFactorValRef = useRef(null);
  const canIndicatorRef = useRef(null);
  const presetLongPressRef = useRef(null); // Таймер long-press для таба пресета
  const customColorInputRef = useRef(null); // Реф для нативного input="color"
  const clearConfirmTimerRef = useRef(null); // Таймер для автоматического закрытия алерта очистки
  const autoHideTimerRef = useRef(null); // Таймер для автозакрытия шторок в фуллскрине
  const swiperStateRef = useRef({ lastIndex: 1 }); // Ref for swiper infinite scroll tracking

  const resetAutoHideTimer = useCallback(() => {
    if (!isFullscreen) return;
    if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
    autoHideTimerRef.current = setTimeout(() => {
      setShowMobileSettingsModal(false);
      setShowColorPickerFor(null);
    }, 5000);
  }, [isFullscreen]);

  useEffect(() => {
    setIsMounted(true);
    const checkMobile = () => setIsMobileView(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (showMobileSettingsModal || showColorPickerFor) {
      resetAutoHideTimer();
    } else {
      if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
    }
    return () => {
      if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
    };
  }, [showMobileSettingsModal, showColorPickerFor, isFullscreen, resetAutoHideTimer]);

  // ============================================================================
  // ЭФФЕКТЫ ИНИЦИАЛИЗАЦИИ
  // ============================================================================

  // Restore state from localStorage on mount
  useLayoutEffect(() => {
    // 0. Initial viewport restore logic — set target scroll so the sync-layout hook applies it once
    const storedVp = lsGet(LS.VIEWPORT, null);
    if (storedVp) targetScrollRef.current = storedVp;

    // 1. Initial defaults registration for all presets to avoid logic gaps
    if (presets['Fat Cap']) presetManager.registerDefaultValues('Fat Cap', presets['Fat Cap'].scaleVal, 50, 5000, 2.5);
    if (presets['Universal Cap']) presetManager.registerDefaultValues('Universal Cap', presets['Universal Cap'].scaleVal, 30, 3000, 1.8);
    if (presets['Night Quill Slim Dart']) presetManager.registerDefaultValues('Night Quill Slim Dart', presets['Night Quill Slim Dart'].scaleVal, 10, 1000, 1.2);

    // Read all persisted values from localStorage (client-only, runs after hydration)
    const storedCans = lsGet(LS.CANS, null) ?? defaultCans();
    const storedColor = lsGet(LS.COLOR, '#ff3366');
    const storedActiveCanId = lsGet(LS.ACTIVE_CAN, 5);
    const storedUnlockedPresets = lsGet('sw:unlocked_presets', []);
    const storedPresetRaw = lsGet(LS.PRESET, 'Fat Cap');
    const storedFlatMode = lsGet(LS.FLAT_MODE, false);
    const storedOverrides = lsGet(LS.OVERRIDES, {});

    const FREE_PRESETS = ['Fat Cap', 'Universal Cap', 'Night Quill Slim Dart', 'Roll'];
    const storedPreset = (FREE_PRESETS.includes(storedPresetRaw) || storedUnlockedPresets.includes(storedPresetRaw))
      ? storedPresetRaw
      : 'Fat Cap';

    initSprayEngine({
      canvasId: 'sprayCanvas',
      initialCans: storedCans,
      initialConfig: {
        sprayRadius: presets[storedPreset]?.radiusVal ?? 45,
        dotsPerTick: presets[storedPreset]?.densityVal ?? 5600,
        lineScale: presets[storedPreset]?.scaleVal ?? 0.65,
        speedFactor: presets[storedPreset]?.speedFactor ?? 1.5,
        paintMax: 10000,
        currentColor: storedColor,
        activeCanId: storedActiveCanId
      },
      initialPreset: storedPreset,
      presets,
      getTranslation,
      lang,
      transparentBackground: true
    });

    // Sync stored values back into React state (batched in React 18 — single re-render)
    setCansState(storedCans);
    setCurrentColorState(storedColor);
    setActiveCanIdState(storedActiveCanId);
    setActivePresetState(storedPreset);

    // Determine slide type from active can
    const activeCan = storedCans.find(c => c.id === storedActiveCanId);
    const initialType = activeCan?.type || (storedActiveCanId <= 4 ? 'roller' : 'spray');
    setActiveSlideTypeState(initialType);
    if (initialType === 'roller') setLastRollerId(storedActiveCanId);
    else setLastSprayId(storedActiveCanId);
    setFlatModeState(storedFlatMode);
    setUserOverridesState(storedOverrides);

    // Zoom, Only Zoom mode and Grid restoration
    const storedZoom = lsGet(LS.ZOOM, 1);
    const storedOnlyZoom = lsGet(LS.ONLY_ZOOM, false);
    const storedShowGrid = lsGet(LS.SHOW_GRID, false);
    setZoomState(storedZoom);
    setIsOnlyZoomMode(storedOnlyZoom);
    setShowGridState(storedShowGrid);
    setIsPanModeState(true);
    setIsFullscreenState(lsGet(LS.FULLSCREEN, false));

    // Restore slider values: Each preset now has its own saved values in LS.SLIDER_VALS.
    const allStoredSliders = lsGet(LS.SLIDER_VALS, {});
    const storedSliders = allStoredSliders[storedPreset];

    const selectedPreset = presets[storedPreset];
    const mountedActiveCan = storedCans.find(c => c.id === storedActiveCanId);
    const paintLeft = mountedActiveCan ? mountedActiveCan.paintLeft : 10000;
    const dynParams = calculateDynamicParams(paintLeft, storedPreset);
    const ov = storedOverrides[storedPreset] || {};

    const finalRadius = storedSliders ? storedSliders.radius : (ov.radius ?? dynParams.radius);
    const finalDensity = storedSliders ? storedSliders.density : (ov.density ?? dynParams.density);
    const finalSpeedFactor = storedSliders ? storedSliders.speedFactor : (ov.speedFactor ?? dynParams.speedFactor);
    const finalScale = storedSliders ? storedSliders.scale : (ov.scale ?? selectedPreset?.scaleVal ?? 0.65);

    setScaleVal(finalScale);
    setRadiusVal(finalRadius);
    setDensityVal(finalDensity);
    setSpeedFactorVal(finalSpeedFactor);
    setActualSpeed(finalScale);
    setActualRadius(finalRadius);
    setActualDensity(finalDensity);
    setActualSpeedFactor(finalSpeedFactor);

    // Apply flat mode and overrides to the engine
    if (storedFlatMode && window.setFlatMode) window.setFlatMode(storedFlatMode);

    // Update engine with restored slider values immediately
    if (window.updateSprayParams) {
      window.updateSprayParams(
        finalRadius, finalDensity, finalScale, finalSpeedFactor,
        storedPreset,
        selectedPreset?.enableDrips || false,
        selectedPreset?.paintConsumption || 1,
        selectedPreset,
        storedActiveCanId,
        storedColor,
        storedCans
      );
    }

    isInitializedRef.current = true;
  }, []); // Запускается только при монтировании

  const isDbRestoreDoneRef = useRef(false);

  useEffect(() => {
    // Restore preset from PocketBase on mount
    import('./pocketbaseSync').then(({ getActivePresetFromDB }) => {
      getActivePresetFromDB().then((data) => {
        if (data && data.activePreset) {
          setActivePresetState(data.activePreset);
          lsSet(LS.PRESET, data.activePreset);
          if (data.scaleVal !== undefined) setScaleVal(data.scaleVal);
          if (data.radiusVal !== undefined) setRadiusVal(data.radiusVal);
        }
        isDbRestoreDoneRef.current = true;
      }).catch(() => {
        isDbRestoreDoneRef.current = true;
      });
    });
  }, []);

  // Восстановление 2D движка при выходе из 3D-режима
  useEffect(() => {
    if (is3DMode) return; // В 3D режиме движком рулит ClientWall3D.js
    
    // Если мы вернулись в 2D, нужно заново привязаться к #sprayCanvas!
    if (isInitializedRef.current) {
        initSprayEngine({
          canvasId: 'sprayCanvas',
          initialCans: cans,
          initialConfig: {
            sprayRadius: actualRadius,
            dotsPerTick: actualDensity,
            lineScale: scaleVal,
            speedFactor: speedFactorVal,
            paintMax: 10000,
            currentColor: currentColor,
            activeCanId: activeCanId
          },
          initialPreset: activePreset,
          presets,
          getTranslation,
          lang,
          transparentBackground: true
        });
        
        if (flatMode && window.setFlatMode) window.setFlatMode(flatMode);
    }
  }, [is3DMode]); 

  // Canvas persistence — restore from localStorage and save on stroke
  const { notifyStroke, clearPersistedCanvas } = useCanvasPersistence({
    canvasId: 'sprayCanvas',
    engineReady: true,
    lang
  });

  // Engine event callbacks are handled within useCanvasPersistence
  useEffect(() => {
    // No-op here, callbacks are set in the hook
  }, []);

  // ============================================================================
  // ХУКИ БИЗНЕС-ЛОГИКИ
  // ============================================================================

  // Общий объект setters для параметров движка (передаётся в хуки)
  const paramSetters = {
    setScaleVal, setRadiusVal, setDensityVal, setSpeedFactorVal,
    setActualRadius, setActualDensity, setActualSpeed, setActualSpeedFactor,
  };
  const paramRefs = { scaleValRef, radiusValRef, densityValRef, speedFactorValRef };
  const toolbarSetters = { setToolbarSprayColor, setToolbarSprayPct, setToolbarRollerColor };

  // Мост движок → мобильный тулбар (обновление % краски)
  usePaintBridge(cans, setToolbarSprayPct, setToolbarRollerPct);

  // Управление баллончиками и цветом
  const { selectCan: baseSelectCan, applySelectedColor, applyCustomColorBatch, cancelColorSelection, addNewCan } = useCanManager({
    cans, setCans, activeCanId, setActiveCanId,
    currentColor, setCurrentColor,
    activePreset, userOverrides, presets,
    showColorPickerFor, setShowColorPickerFor,
    tempColor, setTempColor,
    paramSetters, toolbarSetters,
    isPreviewMode, previewCanId,
    onEnterMultiPreviewMode: (colors, canId, targetCan) => {
      setMultiPreviewColors(colors);
      setPreviewColor(colors[0].hex);
      setPreviewColorName(colors[0].name || null);
      setPreviewCanId(canId);
      setPreviewTargetCan(targetCan);
      setActiveSlideTypeState(targetCan?.type || (canId <= 4 ? 'roller' : 'spray'));
      setIsPreviewMode(true);
      setShowMobileToolSheet(false);
      const pCanvas = previewCanvasRef.current;
      if (pCanvas) {
        const pCtx = pCanvas.getContext('2d');
        pCtx.clearRect(0, 0, pCanvas.width, pCanvas.height);
        if (window.setPreviewMode) window.setPreviewMode(true, pCtx);
      }
      if (window.setActiveCan) window.setActiveCan(canId, colors[0].hex);
      if (targetCan?.type === 'spray') toolbarSetters.setToolbarSprayColor(colors[0].hex);
      else if (targetCan?.type === 'roller') toolbarSetters.setToolbarRollerColor(colors[0].hex);
    },
    onExitPreviewMode: () => {
      // Inline cancel — all deps available before useCanManager call
      if (window.setPreviewMode) window.setPreviewMode(false);
      if (previewCanvasRef.current) {
        const pCtx = previewCanvasRef.current.getContext('2d');
        pCtx.clearRect(0, 0, previewCanvasRef.current.width, previewCanvasRef.current.height);
      }
      if (window.updateSprayColor) window.updateSprayColor(currentColor);
      if (window.syncPaintState) window.syncPaintState(cans);
      setIsPreviewMode(false);
      setPreviewColor(null);
      setMultiPreviewColors(null);
      setPreviewColorName(null);
      setPreviewCanId(null);
      setPreviewTargetCan(null);

      // Restore toolbar icons to their actual state
      const sprayCan = cans.find(c => c.type === 'spray' || c.id >= 5);
      const rollerCan = cans.find(c => c.type === 'roller' || c.id <= 4);
      if (sprayCan) toolbarSetters.setToolbarSprayColor(sprayCan.color);
      if (rollerCan) toolbarSetters.setToolbarRollerColor(rollerCan.color);
    },
    onEnterPreviewMode: (colorToApply, canId, targetCan, colorName) => {
      // Enter test drive mode instead of committing the color
      setPreviewColor(colorToApply);
      // Use the name passed directly, or fall back to the ref set before applySelectedColor
      setPreviewColorName(colorName || pendingColorNameRef.current || null);
      pendingColorNameRef.current = null; // clear after use
      setPreviewCanId(canId);
      setPreviewTargetCan(targetCan);
      setIsPreviewMode(true);
      setShowMobileToolSheet(false);
      // Activate the engine's preview canvas targeting
      const pCanvas = previewCanvasRef.current;
      if (pCanvas) {
        // Clear previous preview content
        const pCtx = pCanvas.getContext('2d');
        pCtx.clearRect(0, 0, pCanvas.width, pCanvas.height);
        if (window.setPreviewMode) window.setPreviewMode(true, pCtx);
      }
      // Switch engine's activeCanId so paint consumption tracks the preview can
      if (window.setActiveCan) {
        window.setActiveCan(canId, colorToApply);
      } else if (window.updateSprayColor) {
        window.updateSprayColor(colorToApply);
      }

      // CRITICAL: Synchronize engine immediately with the preview color
      if (window.updateSprayParams) {
        const selectedPreset = presets[activePreset];
        const paintLeft = targetCan ? targetCan.paintLeft : 10000;
        const dynamicParams = calculateDynamicParams(paintLeft, activePreset);
        const overridenSliders = userOverrides[activePreset] || {};
        const finalRadius = overridenSliders.radius ?? dynamicParams.radius;
        const finalDensity = overridenSliders.density ?? dynamicParams.density;
        const finalSpeedFactor = overridenSliders.speedFactor ?? dynamicParams.speedFactor;
        const finalScale = overridenSliders.scale ?? selectedPreset.scaleVal;

        window.updateSprayParams(
          finalRadius, finalDensity, finalScale, finalSpeedFactor,
          activePreset,
          selectedPreset?.enableDrips || false,
          selectedPreset?.paintConsumption || 1,
          selectedPreset,
          canId, colorToApply, cans
        );
      }
      // Update toolbar preview
      if (targetCan?.type === 'spray') toolbarSetters.setToolbarSprayColor(colorToApply);
      else if (targetCan?.type === 'roller') toolbarSetters.setToolbarRollerColor(colorToApply);
    },
  });

  // Category-aware selection wrapper
  const selectCan = useCallback((id) => {
    baseSelectCan(id);
    const can = cans.find(c => c.id === id);
    if (can) {
      const type = can.type || (can.id <= 4 ? 'roller' : 'spray');
      setActiveSlideTypeState(type);
      if (type === 'roller') {
        setLastRollerId(id);
        lsSet(LS.LAST_ROLLER, id);
      } else {
        setLastSprayId(id);
        lsSet(LS.LAST_SPRAY, id);
      }
    }
  }, [baseSelectCan, cans]);

  // Handle Type Change (Swiping)
  const setActiveSlideType = useCallback((newType) => {
    setActiveSlideTypeState(newType);
    const targetId = newType === 'roller' ? lastRollerId : lastSprayId;
    if (activeCanId !== targetId) {
      baseSelectCan(targetId, { autoSwitchMode: true });
    }
  }, [lastRollerId, lastSprayId, activeCanId, baseSelectCan]);


  // Управление пресетами: применение и сброс
  const { applyPreset, handleResetPreset: originalHandleResetPreset } = useSprayControls({
    presets, activePreset, setActivePreset,
    activeCanId, cans, currentColor,
    userOverrides, setUserOverrides,
    presetManager, paramSetters, paramRefs,
    isPreviewMode, previewColor, previewCanId,
  });

  const handleResetPreset = () => {
    // Clear per-preset sliders in LS
    const all = lsGet(LS.SLIDER_VALS, {});
    if (all[activePreset]) {
      delete all[activePreset];
      lsSet(LS.SLIDER_VALS, all);
    }
    // Call original reset logic
    originalHandleResetPreset();
  };

  // Простой обработчик изменения цвета в пикере
  const handleColorChange = (e) => setTempColor(e.target.value);

  // Циклическое переключение пресетов (мобильный тулбар, short press)
  const cyclePreset = () => setActivePreset(presetManager.getNextPreset(activePreset));

  // Применяем пресет при его внешнем изменении (через settings modal).
  // Skip the very first call on mount — useLayoutEffect already restored values from LS.SLIDER_VALS.
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      return;
    }
    const isFlat = presets[activePreset]?.flat || false;
    setFlatMode(isFlat);
    applyPreset(activePreset);
  }, [activePreset]); // eslint-disable-line react-hooks/exhaustive-deps

  // Синхронизируем flatMode с движком
  useEffect(() => {
    if (window.setFlatMode) window.setFlatMode(flatMode);
  }, [flatMode]);

  const prevPresetRef = useRef(activePreset);

  // Persist slider values to localStorage and PocketBase
  useEffect(() => {
    if (!isDbRestoreDoneRef.current) return; // Prevent overwriting DB before restore completes
    if (isAdjusting) return; // Wait until adjustment ends to save

    const presetChanged = prevPresetRef.current !== activePreset;
    prevPresetRef.current = activePreset;

    // Save to direct sliders storage (per-preset)
    const allSliders = lsGet(LS.SLIDER_VALS, {});
    allSliders[activePreset] = {
      radius: actualRadius,
      density: actualDensity,
      scale: actualSpeed,
      speedFactor: actualSpeedFactor,
    };
    lsSet(LS.SLIDER_VALS, allSliders);

    // Sync to PocketBase in the background
    // Dynamic import to avoid SSR errors with browser-only functions
    import('./pocketbaseSync').then(({ syncPresetToDB }) => {
      syncPresetToDB(activePreset, actualSpeed, actualRadius, presetChanged);
    });

  }, [actualRadius, actualDensity, actualSpeed, actualSpeedFactor, activePreset, isAdjusting]);

  // Handle showing/hiding reset button with delay to prevent shifts while dragging
  useEffect(() => {
    if (isAdjusting) {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    } else {
      // If we are NOT dragging and the button should be visible (e.g. on load), show it immediately.
      // We only want the delay when we STOP dragging to let the bouncy movement settle.
      const shouldShow = presetManager.hasPresetChanges;
      if (shouldShow && !showResetBtn) {
        // First calculation or state mismatch - show immediately
        setShowResetBtn(true);
      } else {
        // Standard transition/hide - use a tiny delay for smoothness
        resetTimerRef.current = setTimeout(() => {
          setShowResetBtn(shouldShow);
        }, 120);
      }
    }
    return () => { if (resetTimerRef.current) clearTimeout(resetTimerRef.current); };
  }, [isAdjusting, presetManager.hasPresetChanges, showResetBtn]);

  // ============================================================================
  // PREVIEW / TEST DRIVE MODE HANDLERS
  // ============================================================================

  // Activate fade hook for preview canvas
  usePreviewFade({ previewCanvasRef, isPreviewMode });

  // Confirm: commit the tested color to the can set
  const confirmPreviewColor = useCallback(() => {
    if (!isPreviewMode || !previewColor || !previewCanId) return;
    const pCanvas = previewCanvasRef.current;
    const mainCanvas = document.getElementById('sprayCanvas');
    if (pCanvas && mainCanvas) {
      const mainCtx = mainCanvas.getContext('2d');
      // Merge preview drawing onto main canvas
      mainCtx.drawImage(pCanvas, 0, 0);
      // Clear preview canvas
      const pCtx = pCanvas.getContext('2d');
      pCtx.clearRect(0, 0, pCanvas.width, pCanvas.height);
    }
    // Disable preview targeting
    if (window.setPreviewMode) window.setPreviewMode(false);

    // Action: commit the color(s) to the cans
    if (multiPreviewColors && multiPreviewColors.length > 0) {
      applyCustomColorBatch(multiPreviewColors, previewCanId);
    } else if (previewColor) {
      applySelectedColor(previewColor, previewCanId);
    }

    setIsPreviewMode(false);
    setPreviewColor(null);
    setMultiPreviewColors(null);
    setPreviewColorName(null);
    setPreviewCanId(null);
    setPreviewTargetCan(null);
  }, [isPreviewMode, previewColor, previewCanId, multiPreviewColors, applySelectedColor, applyCustomColorBatch]);

  // Cancel: discard the test color
  const cancelPreviewMode = useCallback(() => {
    if (window.setPreviewMode) window.setPreviewMode(false);
    const pCanvas = previewCanvasRef.current;
    if (pCanvas) {
      const pCtx = pCanvas.getContext('2d');
      pCtx.clearRect(0, 0, pCanvas.width, pCanvas.height);
    }
    // Restore original color in engine
    if (window.updateSprayColor) window.updateSprayColor(currentColor);
    // Restore toolbar
    const activeCan = cans.find(c => c.id === activeCanId);
    if (activeCan) {
      if (activeCan.type === 'spray') toolbarSetters.setToolbarSprayColor(activeCan.color);
      else if (activeCan.type === 'roller') toolbarSetters.setToolbarRollerColor(activeCan.color);
    }

    setIsPreviewMode(false);
    setPreviewColor(null);
    setMultiPreviewColors(null);
    setPreviewColorName(null);
    setPreviewCanId(null);
    setPreviewTargetCan(null);
  }, [currentColor, cans, activeCanId]);

  // ============================================================================
  // РЕНДЕРИНГ ИНТЕРФЕЙСА
  // ============================================================================

  // Расчет динамической высоты панели управления
  const sprayCount = cans.filter(c => c.type === 'spray' || c.id > 4).length;
  const hasAddButton = sprayCount < 26;
  const totalGridItems = sprayCount + (hasAddButton ? 1 : 0);
  const rowsCount = Math.ceil(totalGridItems / 4);

  // Dynamic rows based on active view in swiper
  const activeRowsCount = activeSlideType === 'roller' ? 1 : rowsCount;

  // Базовые константы высоты для расчетов
  const rowHeight = 72;
  const indicatorPadding = 10;
  const dotsAreaHeight = 24; // Fixed dots height

  const basePanelHeight = (isPreviewMode && !isAutoGhost)
    ? 204
    : Math.min(700, 284 + (activeRowsCount - 2) * rowHeight + ((isPreviewMode && !isAutoGhost) ? 0 : dotsAreaHeight));
  const expandedHeight = 320 + (activeRowsCount - 2) * rowHeight + ((isPreviewMode && !isAutoGhost) ? 0 : dotsAreaHeight);
  const panelHeight = isControlsPanelExpanded && !(isPreviewMode && !isAutoGhost) ? Math.max(expandedHeight, basePanelHeight) : basePanelHeight;

  // Dynamic height for can-indicator itself (tools + dots)
  const canIndicatorHeight = activeRowsCount * rowHeight + indicatorPadding + dotsAreaHeight;

  // Определение текста пресета в зависимости от выбранного инструмента
  const activeCan = cans.find(c => c.id === activeCanId);
  const isRoller = activeCan?.type === 'roller' || activeCanId <= 4;
  let presetDescription = presets[activePreset]?.description;
  if (isRoller) {
    if (activePreset === 'Fat Cap') presetDescription = getTranslation(lang, 'presetBigRoller');
    else if (activePreset === 'Universal Cap') presetDescription = getTranslation(lang, 'presetMidRoller');
    else if (activePreset === 'Night Quill Slim Dart') presetDescription = getTranslation(lang, 'presetSmallRoller');
  } else {
    if (activePreset === 'Fat Cap') presetDescription = getTranslation(lang, 'presetBigSpray');
    else if (activePreset === 'Universal Cap') presetDescription = getTranslation(lang, 'presetMidSpray');
    else if (activePreset === 'Night Quill Slim Dart') presetDescription = getTranslation(lang, 'presetSmallSpray');
  }

  // --------------------------------------------------------------------------
  // Обработчик зума на колесико/pinch (ctrlKey/metaKey) с блокировкой браузера
  // --------------------------------------------------------------------------
  const handleNativeWheel = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      // Предотвращаем дефолтный браузерный zoom (работает, т.к. лисенер non-passive)
      e.preventDefault();

      const zoomSensitivity = 0.005;
      const zoomDelta = -e.deltaY * zoomSensitivity;

      const container = document.getElementById('canvas-container');
      if (!container) return;

      // Проверяем, что событие произошло над холстом или оверлеем перемещения
      const isOverCanvas = e.target.closest('#canvas-container') || (e.target.classList && e.target.classList.contains('pan-mode-overlay'));
      if (!isOverCanvas) return;

      const rect = container.getBoundingClientRect();

      const pointerX = e.clientX - rect.left;
      const pointerY = e.clientY - rect.top;

      const currentScrollLeft = container.scrollLeft;
      const currentScrollTop = container.scrollTop;

      setZoom(prevZoom => {
        const newZoom = Math.max(0.2, Math.min(prevZoom + zoomDelta, 3.0));

        const contentX = currentScrollLeft + pointerX;
        const contentY = currentScrollTop + pointerY;
        const scaleChange = newZoom / prevZoom;

        // Сохраняем целевой скролл для применения ПОСЛЕ рендера React (когда обертка вырастет)
        targetScrollRef.current = {
          left: contentX * scaleChange - pointerX,
          top: contentY * scaleChange - pointerY
        };

        return newZoom;
      });
    }
  }, []);

  // Вешаем не-пассивный слушатель колесика на window, чтобы зум работал и в режиме Grab (через оверлей)
  useEffect(() => {
    window.addEventListener('wheel', handleNativeWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleNativeWheel);
  }, [handleNativeWheel]);

  // Синхронно применяем скролл после того, как React обновил DOM с новыми размерами canvas-wrapper
  useLayoutEffect(() => {
    if (targetScrollRef.current) {
      const container = document.getElementById('canvas-container');
      if (container) {
        container.scrollLeft = targetScrollRef.current.left;
        container.scrollTop = targetScrollRef.current.top;
      }
      targetScrollRef.current = null;
    }
  });

  // ────────────────────────────────────────────────────────────────────────────
  // Логика переключения пресетов зума (100% -> 58% -> 25% -> 100%)
  // ────────────────────────────────────────────────────────────────────────────
  const getNextZoomPreset = useCallback((currentZoom) => {
    const z = Math.round(currentZoom * 100);
    if (z === 100) return 0.58;
    if (z === 58) return 0.25;
    if (z === 25) return 1.0;
    return 1.0; // Любое другое значение сбрасывает в 100%
  }, []);

  const handleZoomTextClick = useCallback(() => {
    setZoom(getNextZoomPreset(zoom));
    setHoverZoomText(null); // Сбрасываем ховер после клика
  }, [zoom, getNextZoomPreset]);

  const handleZoomTextMouseEnter = useCallback(() => {
    const nextZoom = getNextZoomPreset(zoom);
    setHoverZoomText(`${Math.round(nextZoom * 100)}%`);
  }, [zoom, getNextZoomPreset]);

  const handleZoomTextMouseLeave = useCallback(() => {
    setHoverZoomText(null);
  }, []);

  const handleClear = useCallback(() => {
    if (isResetting) return;
    setIsResetting(true);

    if (window.clearCanvas) {
      window.clearCanvas();
    }
    if (window.clear3DWall) {
      window.clear3DWall();
    }
    clearPersistedCanvas(); // Remove saved canvas from localStorage
    lsDel(LS.VIEWPORT);
    lsDel(LS.ZOOM);
    targetScrollRef.current = { left: 0, top: 0 };
    setZoom(1);

    setTimeout(() => {
      setIsResetting(false);
    }, 450);
  }, [isResetting, clearPersistedCanvas, setZoom]);

  const handleResetRequest = useCallback((isHotkey = false) => {
    if (showClearConfirmRef.current) {
      if (isHotkey) {
        // Если это хоткей и алерт уже открыт — закрываем (новая логика: хоткей R переключает алерт)
        setShowClearConfirm(false);
        if (clearConfirmTimerRef.current) {
          clearTimeout(clearConfirmTimerRef.current);
          clearConfirmTimerRef.current = null;
        }
      } else {
        // Если это повторный клик по кнопке — просто обновляем таймер (не очищаем!)
        if (clearConfirmTimerRef.current) clearTimeout(clearConfirmTimerRef.current);
        clearConfirmTimerRef.current = setTimeout(() => {
          setShowClearConfirm(false);
          clearConfirmTimerRef.current = null;
        }, 30000);
      }
    } else {
      // Первое нажатие/клик — открываем алерт
      setShowClearConfirm(true);
      // Авто-закрытие через 30 секунд
      if (clearConfirmTimerRef.current) clearTimeout(clearConfirmTimerRef.current);
      clearConfirmTimerRef.current = setTimeout(() => {
        setShowClearConfirm(false);
        clearConfirmTimerRef.current = null;
      }, 30000);
    }
  }, [setShowClearConfirm]);

  // Обработка глобальных горячих клавиш (тема и пресеты)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Игнорируем нажатия внутри полей ввода
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;

      // 'T' или 't' (en) / 'Е' или 'е' (ru) - учитываем код клавиши
      if (e.code === 'KeyT') {
        const nextMode = actualTheme === 'dark' ? 'light' : 'dark';
        changeTheme(nextMode);
      }

      // Hotkeys for Presets
      if (e.code === 'KeyQ') { // Big
        if (activePreset === 'Big') {
          setFlatMode(!flatMode);
        } else {
          setActivePreset('Big');
        }
      } else if (e.code === 'KeyW') { // Mid
        if (activePreset === 'Mid') {
          setFlatMode(!flatMode);
        } else {
          setActivePreset('Mid');
        }
      } else if (e.code === 'KeyE') { // Small
        if (activePreset === 'Small') {
          setFlatMode(!flatMode);
        } else {
          setActivePreset('Small');
        }
      } else if (e.code === 'KeyR') { // Clear Canvas
        handleResetRequest(true); // Передаем true для хоткея
      } else if (e.code === 'KeyY') { // Toggle Grid
        setShowGrid(!showGrid);
      } else if (e.code === 'Digit1') { // 100% Zoom
        if (is3DMode) setZoom3D(1.0);
        else setZoom(1.0);
      } else if (e.code === 'Digit2') { // 58% Zoom
        if (is3DMode) setZoom3D(0.58);
        else setZoom(0.58);
      } else if (e.code === 'Digit3') { // 25% Zoom
        if (is3DMode) setZoom3D(0.25);
        else setZoom(0.25);
      } else if (e.code === 'Enter') {
        if (showClearConfirmRef.current) {
          handleClear();
          setShowClearConfirm(false);
          if (clearConfirmTimerRef.current) {
            clearTimeout(clearConfirmTimerRef.current);
            clearConfirmTimerRef.current = null;
          }
        }
      } else if (e.code === 'Escape') {
        if (showClearConfirmRef.current) setShowClearConfirm(false);
        if (showThemeModal) setShowThemeModal(false);
        if (showLangModal) setShowLangModal(false);
        if (showHotkeysModal) setShowHotkeysModal(false);
        if (showChangelogModal) setShowChangelogModal(false);
        if (showMobileTopMenu) setShowMobileTopMenu(false);
        if (showMobileCanvasSheet) setShowMobileCanvasSheet(false);
        if (showMobileToolSheet) setShowMobileToolSheet(false);
        if (showMobileSettingsModal) setShowMobileSettingsModal(false);
        if (showColorPickerFor) setShowColorPickerFor(null);
      } else if (e.code === 'Backslash') { // Full Screen Mode toggle
        e.preventDefault();
        setIsFullscreen(!isFullscreen);
      } else if (e.code === 'Slash') { // Mode toggle (Grab/Spray)
        e.preventDefault();
        setIsPanMode(!isPanMode);
      } else if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.code === 'KeyZ') {
        e.preventDefault();
        if (typeof window.onSprayRedo === 'function') window.onSprayRedo();
      } else if ((e.ctrlKey || e.metaKey) && e.code === 'KeyZ') {
        e.preventDefault();
        if (typeof window.onSprayUndo === 'function') window.onSprayUndo();
      } else if ((e.ctrlKey || e.metaKey) && e.code === 'KeyS') {
        e.preventDefault();
        // Разделяем сохранение для 3D и 2D, чтобы избежать конфликтов при перезаписи хуков
        if (is3DMode && typeof window.performInstant3DWallSave === 'function') {
          window.performInstant3DWallSave();
        } else if (typeof window.performInstantWallSave === 'function') {
          window.performInstantWallSave();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    actualTheme, changeTheme, activePreset, flatMode, setFlatMode, handleResetRequest, handleClear, setShowClearConfirm, showGrid, setShowGrid,
    showThemeModal, showLangModal, showHotkeysModal, showChangelogModal, showMobileTopMenu,
    showMobileCanvasSheet, showMobileToolSheet, showMobileSettingsModal,
    showColorPickerFor, setZoom, isFullscreen, setIsFullscreen, isPanMode, setIsPanMode, 
    is3DMode, zoom3D, setZoom3D
  ]);

  const renderPreviewModeBar = () => {
    return (
      <div className="preview-mode-bar" style={{}}>
        <>
          {multiPreviewColors ? (
            <div className="multi-preview-list" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', marginRight: 'var(--space-2)' }}>
              {multiPreviewColors.map((cp, idx) => {
                const isSelected = previewColor === cp.hex;
                return (
                  <ColorIndicator
                    key={idx}
                    color={cp.hex}
                    percent={100}
                    showPercent={false}
                    shape="circle"
                    size="sm"
                    style={{
                      transform: isSelected ? 'scale(1.2)' : 'scale(1)',
                      transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      cursor: 'pointer',
                      border: isSelected ? '2px solid var(--fg-default)' : 'none',
                      boxShadow: isSelected ? '0 0 8px var(--primary-muted)' : 'none'
                    }}
                    onClick={() => {
                      setPreviewColor(cp.hex);
                      setPreviewColorName(cp.name || null);
                      if (window.setActiveCan) window.setActiveCan(previewCanId, cp.hex);
                      if (previewTargetCan?.type === 'spray') toolbarSetters.setToolbarSprayColor(cp.hex);
                      else if (previewTargetCan?.type === 'roller') toolbarSetters.setToolbarRollerColor(cp.hex);
                    }}
                    title={cp.name || cp.hex}
                  />
                );
              })}
            </div>
          ) : (
            <ColorIndicator
              className="preview-indicator-override"
              color={previewColor || '#fff'}
              percent={100}
              showPercent={false}
              shape="circle"
              size="sm"
              onClick={() => {
                // Re-open color picker for the current preview can
                setShowColorPickerFor(previewCanId);
                setTempColor(previewColor || '#ff3366');
              }}
              title="Click to change color"
            />
          )}

          <span className="preview-mode-label">
            {!multiPreviewColors && previewColorName
              ? previewColorName
              : multiPreviewColors
                ? getTranslation(lang, 'previewModeMultiLabel') || 'Test Drive'
                : getTranslation(lang, 'previewModeCustomLabel')}
          </span>
          <button className="preview-confirm-btn" onClick={confirmPreviewColor}>
            ✓ {getTranslation(lang, 'btnKeepColor')}
          </button>
          <button className="preview-cancel-btn" onClick={cancelPreviewMode}>
            ✕
          </button>
        </>
      </div>
    );
  };

  // --- Вспомогательный компонент Swiper ---
  const InfiniteCanSwiper = ({ cans, activeCanId, selectCan, addNewCan, type, setType, isLarge, lang, showDots = false }) => {
    const swiperRef = useRef(null);
    const dragRef = useRef(false);
    const slides = [
      { id: 'r_clone', type: 'roller', items: cans.filter(c => c.type === 'roller') },
      { id: 's_main', type: 'spray', items: cans.filter(c => (c.type === 'spray' || c.id > 4)) },
      { id: 'r_main', type: 'roller', items: cans.filter(c => c.type === 'roller') },
      { id: 's_clone', type: 'spray', items: cans.filter(c => (c.type === 'spray' || c.id > 4)) },
    ];

    useLayoutEffect(() => {
      const el = swiperRef.current;
      if (el) {
        const w = el.offsetWidth;
        if (w > 0) {
          const currentIndex = Math.round(el.scrollLeft / w);
          const currentType = (currentIndex === 1 || currentIndex === 3) ? 'spray' : 'roller';
          if (currentType !== type) {
            const targetIndex = type === 'spray' ? 1 : 2;
            el.scrollLeft = targetIndex * w;
            swiperStateRef.current.lastIndex = targetIndex;
          }
        }
      }
    }, [type]);

    const onScroll = (e) => {
      const el = e.currentTarget;
      const w = el.offsetWidth;
      if (w <= 0) return;
      const x = el.scrollLeft;
      const index = Math.round(x / w);

      if (index !== swiperStateRef.current.lastIndex) {
        swiperStateRef.current.lastIndex = index;
        const newType = (index === 1 || index === 3) ? 'spray' : 'roller';
        if (newType !== type) setType(newType);

        // Silent jump for infinity logic - robust two-way jumping
        if (index === 0) { // Roller clone -> Roller main (2)
          el.scrollLeft = 2 * w + (el.scrollLeft % w);
          swiperStateRef.current.lastIndex = 2;
        } else if (index === 3) { // Spray clone -> Spray main (1)
          el.scrollLeft = 1 * w + (el.scrollLeft % w);
          swiperStateRef.current.lastIndex = 1;
        }
      }
    };

    const handleWheel = (e) => {
      e.stopPropagation(); // Block bubbling to window (prevents Mac back/forward)
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      const el = swiperRef.current;
      if (el) el.scrollLeft += e.deltaY;
    };

    const handleGrab = (e) => {
      if (e.pointerType !== 'mouse') return;
      const el = e.currentTarget;
      el.style.cursor = 'grabbing';
      const startX = e.pageX;
      const scrollStart = el.scrollLeft;
      dragRef.current = false;
      const onMove = (em) => {
        if (Math.abs(em.pageX - startX) > 5) {
          dragRef.current = true;
        }
        el.scrollLeft = scrollStart - (em.pageX - startX);
      };
      const onUp = () => {
        el.style.cursor = 'grab';
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        setTimeout(() => { dragRef.current = false; }, 50);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    };

    return (
      <div className="can-swiper-container" onPointerDown={handleGrab}>
        <div
          className="can-swiper-viewport"
          ref={swiperRef}
          onScroll={onScroll}
          onWheel={handleWheel}
          style={{ height: (type === 'roller' ? 1 : rowsCount) * rowHeight, overflowY: 'hidden' }}
        >
          {slides.map((slide) => (
            <div key={slide.id} className="can-swiper-slide">
              <div className={`cans-grid ${slide.type}s-group`}>
                {slide.items.map(can => {
                  const percentage = Math.round((can.paintLeft / 10000) * 100);
                  const isRoller = slide.type === 'roller';
                  return (
                    <div key={`${slide.id}-${can.id}`} className="can-grid-cell">
                      <ColorIndicator
                        color={can.color}
                        percent={percentage}
                        isActive={activeCanId === can.id}
                        isEmpty={!can.color}
                        shape={isRoller ? "rect" : "circle"}
                        size={isRoller ? "roller" : "md"} // Standard size for both sidebar and mobile
                        onClick={() => {
                          if (!dragRef.current) selectCan(can.id);
                        }}
                        className="mobile-tool-indicator"
                        title={isRoller ? `${getTranslation(lang, 'tooltipRollerText')} ${can.id}` : (can.color ? `${getTranslation(lang, 'tooltipCanText')} ${can.id - 4}${can.name ? ` (${can.name})` : ''}` : `${getTranslation(lang, 'tooltipEmptyCan')} ${can.id - 4}`)}
                      />
                    </div>
                  );
                })}
                {slide.type === 'spray' && slide.items.length < 26 && (
                  <div className="can-grid-cell">
                    <Button
                      shape="circle" variant="outline" onClick={() => {
                        if (!dragRef.current) addNewCan();
                      }}
                      style={{ width: '48px', height: '48px', fontSize: '16px' }} // Unified large size
                    > + </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {showDots && (
          <div className="can-swiper-dots">
            <div className={`can-swiper-dot ${type === 'spray' ? 'active' : ''}`} />
            <div className={`can-swiper-dot ${type === 'roller' ? 'active' : ''}`} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`spraytest-container ${isFullscreen ? 'fullscreen-mode' : ''}`} style={{
      position: 'relative',
      width: '100vw',
      minWidth: '100vw',
      height: '100vh',
      backgroundColor: 'var(--surface-page)',
      overscrollBehavior: 'none'
    }}>
      <link rel="preload" href="/models/CanMiniCut-opt.glb" as="fetch" crossOrigin="anonymous" />

      {/* Контейнер для canvas */}
      <div id="canvas-container">
        {is3DMode ? (
          <div style={{ width: '100%', height: '100%', overflow: 'hidden', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
             <ClientWall3D 
                lang={lang} 
                isIntegrated={true} 
                isPanModeExternal={isPanMode} 
                setIsPanModeExternal={setIsPanMode}
                isSpacePressedExternal={isSpacePressed}
                zoomExternal={zoom3D}
                onZoomChangeExternal={setZoom3D}
                currentColor={isPreviewMode ? previewColor : currentColor}
                activePreset={activePreset}
                activeCanId={activeCanId}
                cans={cans}
             />
          </div>
        ) : (
          <>
        {/* Промежуточный контейнер для создания браузерных скроллов */}
        <div
          className="zoom-space-maker"
          style={{
            width: `${4000 * zoom}px`,
            height: `${1600 * zoom}px`
          }}
        >
          {/* Сама обертка холста (жестко 4000x1600, масштабируется визуально через transform) */}
          <div className="canvas-wrapper" style={{
            width: '4000px',
            height: '1600px',
            transform: `scale(${zoom})`,
            pointerEvents: isPanning ? 'none' : 'auto',
          }}>
            <canvas
              id="sprayCanvas"
              width="4000"
              height="1600"
              style={{ 
                width: '100%', 
                height: '100%'
              }}
            ></canvas>
            {/* Preview canvas - overlaid on main canvas for test drive mode */}
            <canvas
              ref={previewCanvasRef}
              id="previewCanvas"
              width="4000"
              height="1600"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                borderRadius: 'inherit',
              }}
            ></canvas>
            <div className="canvas-grid-overlay" style={{ opacity: showGrid ? 1 : 0 }}></div>
          </div>
        </div>
        </>
        )}
      </div>

      {/* Оверлей при скроллинге (индикация) */}
      <div className="scroll-overlay"></div>
      
      <style>{`
        ${(isSpacePressed || isPanMode || is3DMode) ? '#customCursor { display: none !important; }' : ''}
        ${(isSpacePressed || isPanMode) ? `
          #canvas-container, #canvas-container *, .pan-mode-overlay { 
            cursor: grab !important; 
          }
          #canvas-container:active, #canvas-container *:active, .pan-mode-overlay:active {
            cursor: grabbing !important;
          }
        ` : ''}
      `}</style>
      <div id="customCursor"></div>

      {/* Floating Bar for Fullscreen mode & Mobile */}
      {isMounted && (isFullscreen || isMobileView) && (
        <div className="floating-controls-bar">
          <FloatingToolbar
            activeColor={isPreviewMode ? previewColor : (activeSlideType === 'roller' ? toolbarRollerColor : currentColor)}
            activeColorPercent={activeCanId === null ? 0 : Math.round((cans.find(c => c.id === activeCanId)?.paintLeft || 0) / 10000 * 100)}
            activeColorShape={(previewTargetCan?.type || activeSlideType) === 'roller' ? "rect" : "circle"}
            onColorClick={() => {
              const wasOpen = showMobileToolSheet;
              closeAllModals();
              if (!wasOpen) setShowMobileToolSheet(true);
            }}
            presetName={getTranslation(lang, `presetName${activePreset}`)}
            isFlatMode={flatMode}
            onPresetClick={() => { closeAllModals(); cyclePreset(); }}
            onPresetChevronClick={(e) => {
              e.stopPropagation();
              const wasOpen = showMobileSettingsModal;
              closeAllModals();
              if (!wasOpen) setShowMobileSettingsModal(true);
            }}
            zoomText={is3DMode ? Math.round(zoom3D * 100) + '%' : (hoverZoomText || Math.round(zoom * 100) + '%')}
            onZoomClick={() => { 
                closeAllModals(); 
                if (is3DMode) {
                   if (zoom3D >= 1.0) setZoom3D(0.58);
                   else if (zoom3D >= 0.58) setZoom3D(0.25);
                   else setZoom3D(1.0);
                } else {
                   handleZoomTextClick(); 
                }
            }}
            onZoomMouseEnter={handleZoomTextMouseEnter}
            onZoomMouseLeave={handleZoomTextMouseLeave}
            onMenuClick={() => {
              if (isSaving) return;
              const wasOpen = showMobileTopMenu;
              closeAllModals();
              if (!wasOpen) setShowMobileTopMenu(true);
            }}
            isSaving={isSaving}
            showPanMode={true}
            isPanMode={isPanMode}
            onPanModeClick={() => setIsPanMode(!isPanMode)}
          />
          {(isPreviewMode && !isAutoGhost) && renderPreviewModeBar()}
        </div>
      )}

      {/* Pan Mode Toggle Button removed and moved to FloatingToolbar */}

      {/* Pan Mode Overlay to prevent drawing and handle dragging */}
      {(!is3DMode && (isPanMode || isSpacePressed)) && (
        <div
          className="pan-mode-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 5, // Just below controls but above canvas
            cursor: 'grab',
            transition: 'cursor 0.1s ease',
            touchAction: 'none'
          }}
          onMouseDown={(e) => {
            triggerEphemeralMinimap();
            const container = document.getElementById('canvas-container');
            if (!container) return;
            const startX = e.pageX - container.offsetLeft;
            const startY = e.pageY - container.offsetTop;
            const scrollLeft = container.scrollLeft;
            const scrollTop = container.scrollTop;

            const onMouseMove = (moveEvent) => {
              triggerEphemeralMinimap();
              const x = moveEvent.pageX - container.offsetLeft;
              const y = moveEvent.pageY - container.offsetTop;
              const walkX = (x - startX) * 1.5;
              const walkY = (y - startY) * 1.5;
              container.scrollLeft = scrollLeft - walkX;
              container.scrollTop = scrollTop - walkY;
            };

            const onMouseUp = () => {
              window.removeEventListener('mousemove', onMouseMove);
              window.removeEventListener('mouseup', onMouseUp);
            };

            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
          }}
          onTouchStart={(e) => {
            triggerEphemeralMinimap();
            const container = document.getElementById('canvas-container');
            if (!container) return;
            const touch = e.touches[0];
            const startX = touch.pageX - container.offsetLeft;
            const startY = touch.pageY - container.offsetTop;
            const scrollLeft = container.scrollLeft;
            const scrollTop = container.scrollTop;

            const onTouchMove = (moveEvent) => {
              triggerEphemeralMinimap();
              const t = moveEvent.touches[0];
              const x = t.pageX - container.offsetLeft;
              const y = t.pageY - container.offsetTop;
              const walkX = (x - startX) * 1.5;
              const walkY = (y - startY) * 1.5;
              container.scrollLeft = scrollLeft - walkX;
              container.scrollTop = scrollTop - walkY;
            };

            const onTouchEnd = () => {
              window.removeEventListener('touchmove', onTouchMove);
              window.removeEventListener('touchend', onTouchEnd);
            };

            window.addEventListener('touchmove', onTouchMove);
            window.addEventListener('touchend', onTouchEnd);
          }}
        >
          {/* Ephemeral Minimap while panning */}
          <div
            onMouseMove={e => e.stopPropagation()}
            onMouseEnter={() => {
              isMinimapHoveredRef.current = true;
              if (minimapHideTimerRef.current) clearTimeout(minimapHideTimerRef.current);
            }}
            onMouseLeave={() => {
              isMinimapHoveredRef.current = false;
              triggerEphemeralMinimap();
            }}
            style={{
              position: 'fixed',
              bottom: '16px',
              right: '16px',
              zIndex: 1000,
              pointerEvents: showEphemeralMinimap ? 'auto' : 'none',
              opacity: showEphemeralMinimap ? 1 : 0,
              transform: showEphemeralMinimap ? 'translateX(0)' : 'translateX(8px)',
              transition: 'opacity 0.2s ease, transform 0.2s ease',
              backgroundColor: 'var(--surface-overlay)',
              padding: '4px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-subtle)',
              backdropFilter: 'var(--glass-blur)',
              WebkitBackdropFilter: 'var(--glass-blur)'
            }}
          >
            <CanvasMinimap
              lang={lang}
              readOnly={false}
              minimapWidth={120} // Ratio 5:2 (4000x1600), for 48px height -> 120px width
              minimapHeight={48}
              zoom={zoom}
              showGrid={showGrid}
            />
          </div>
        </div>
      )}

      {/* Контейнер для панели управления и кнопки сброса */}
      {!isFullscreen && (
        <div
          className="controls-wrapper"
          onPointerDown={e => e.stopPropagation()}
          onTouchStart={e => e.stopPropagation()}
          onWheel={e => e.stopPropagation()}
        >
          <div className="panel-with-toggle">
            {/* Панель управления */}
            <div id="controls-panel" style={{
              height: isControlsPanelExpanded ? 'auto' : `${panelHeight}px`,
              transition: 'height 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
              overflowY: 'auto',
              overflowX: 'hidden',
              position: 'relative'
            }}>
              <div className="preset-info">
                {isSaving ? (
                  <div className="save-spinner-container" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--accent-primary)', height: '18px' }}>
                    <div className="loading-spinner-circle" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                    <span style={{ fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {lang === 'ru' ? 'Сохранение...' : 'Saving...'}
                    </span>
                  </div>
                ) : (
                  presetDescription
                )}
              </div>

              {/* Кнопки выбора пресета */}
              <div className="presets-container" style={{}}>
                <Tab
                  tabs={Object.keys(presets).filter(key => key !== 'Roll').map(key => ({
                    id: key,
                    label: (
                      <>
                        {activePreset === key && flatMode ? <span className="flat-suffix">✹ </span> : null}
                        {getTranslation(lang, `presetName${key}`)}
                      </>
                    )
                  }))}
                  activeId={activePreset}
                  selectedId={flatMode ? activePreset : null}
                  onChange={(tabId) => {
                    applyPreset(tabId);
                  }}
                  onSelect={(tabId) => {
                    setFlatMode(tabId !== null);
                  }}
                  allowSelection={true}
                  fullWidth
                />
              </div>

              {/* Индикатор баллончиков / валиков с листанием */}
              <div className="can-indicator" ref={canIndicatorRef} style={{
                height: isPreviewMode ? 'auto' : `${canIndicatorHeight}px`,
                minHeight: '80px',
                marginBottom: '8px',
                transition: 'height 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
                overflow: 'hidden'
              }}>
                {isPreviewMode && !isAutoGhost ? (
                  renderPreviewModeBar()
                ) : (
                  <InfiniteCanSwiper
                    cans={cans}
                    activeCanId={activeCanId}
                    selectCan={selectCan}
                    addNewCan={addNewCan}
                    type={activeSlideType}
                    setType={setActiveSlideType}
                    isLarge={false}
                    lang={lang}
                    showDots={true}
                  />
                )}
              </div>

              {/* Контролы ручной настройки */}
              <div className="controls-panel-inner">

                {/* Кнопка сброса настроек (перенесена наверх) */}
                <div
                  className="control-group"
                  style={{
                    height: showResetBtn ? 'auto' : '0',
                    opacity: showResetBtn ? 1 : 0,
                    overflow: 'hidden',
                    transition: 'all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    pointerEvents: showResetBtn ? 'auto' : 'none',
                    marginTop: showResetBtn ? '0px' : '-24px',
                    marginBottom: showResetBtn ? '16px' : '0'
                  }}
                >
                  <Button
                    id="resetPresetBtn"
                    variant="outline"
                    className="reset-preset-btn"
                    onClick={handleResetPreset}
                    fullWidth
                  >
                    {getTranslation(lang, 'btnResetPreset')}
                  </Button>
                </div>

                {/* Масштаб линии (Scale) - скрываем для валика */}
                {activeCanId > 4 && (
                  <div className="control-group">
                    <RangeSlider
                      label={`${getTranslation(lang, 'settingLineScale')}`}
                      value={scaleVal}
                      min={0.1}
                      max={1.0}
                      step={0.05}
                      onStart={() => setIsAdjusting(true)}
                      onEnd={() => setIsAdjusting(false)}
                      onChange={(value) => {
                        setScaleVal(value);
                        setActualSpeed(value);
                        // Direct save (per-preset)
                        const all = lsGet(LS.SLIDER_VALS, {});
                        all[activePreset] = { radius: actualRadius, density: actualDensity, scale: value, speedFactor: actualSpeedFactor };
                        lsSet(LS.SLIDER_VALS, all);

                        setUserOverrides(prev => ({
                          ...prev,
                          [activePreset]: {
                            ...(prev[activePreset] || {}),
                            scale: value
                          }
                        }));

                        // Обновление движка
                        if (window.updateSprayParams) {
                          window.updateSprayParams(
                            actualRadius,
                            actualDensity,
                            value,
                            actualSpeedFactor,
                            activePreset,
                            presets[activePreset]?.enableDrips || false,
                            presets[activePreset]?.paintConsumption || 1,
                            presets[activePreset],
                            activeCanId,
                            isPreviewMode ? previewColor : currentColor,
                            cans
                          );
                        }
                      }}
                    />
                  </div>
                )}

                {/* Радиус */}
                {activeCanId > 4 && (
                  <div className="control-group">
                    <RangeSlider
                      label={`${getTranslation(lang, 'settingRadius')}`}
                      value={radiusVal}
                      min={5}
                      max={100}
                      step={1}
                      suffix=" px"
                      onStart={() => setIsAdjusting(true)}
                      onEnd={() => setIsAdjusting(false)}
                      onChange={(value) => {
                        setRadiusVal(value);
                        setActualRadius(value);
                        // Direct save (per-preset)
                        const all = lsGet(LS.SLIDER_VALS, {});
                        all[activePreset] = { radius: value, density: actualDensity, scale: actualSpeed, speedFactor: actualSpeedFactor };
                        lsSet(LS.SLIDER_VALS, all);

                        setUserOverrides(prev => ({
                          ...prev,
                          [activePreset]: {
                            ...(prev[activePreset] || {}),
                            radius: value
                          }
                        }));

                        if (window.updateSprayParams) {
                          window.updateSprayParams(
                            value,
                            actualDensity,
                            actualSpeed,
                            actualSpeedFactor,
                            activePreset,
                            presets[activePreset]?.enableDrips || false,
                            presets[activePreset]?.paintConsumption || 1,
                            presets[activePreset],
                            activeCanId,
                            isPreviewMode ? previewColor : currentColor,
                            cans
                          );
                        }
                      }}
                    />
                  </div>
                )}

                {/* Плотность */}
                <div className="control-group">
                  <RangeSlider
                    label={`${getTranslation(lang, 'settingDensity')}`}
                    value={densityVal}
                    min={50}
                    max={10000}
                    step={10}
                    onStart={() => setIsAdjusting(true)}
                    onEnd={() => setIsAdjusting(false)}
                    onChange={(value) => {
                      if (value < 50) return;
                      setDensityVal(value);
                      setActualDensity(value);
                      // Direct save (per-preset)
                      const all = lsGet(LS.SLIDER_VALS, {});
                      all[activePreset] = { radius: actualRadius, density: value, scale: actualSpeed, speedFactor: actualSpeedFactor };
                      lsSet(LS.SLIDER_VALS, all);

                      setUserOverrides(prev => ({
                        ...prev,
                        [activePreset]: {
                          ...(prev[activePreset] || {}),
                          density: value
                        }
                      }));

                      if (window.updateSprayParams) {
                        window.updateSprayParams(
                          actualRadius,
                          value,
                          actualSpeed,
                          actualSpeedFactor,
                          activePreset,
                          presets[activePreset]?.enableDrips || false,
                          presets[activePreset]?.paintConsumption || 1,
                          presets[activePreset],
                          activeCanId,
                          isPreviewMode ? previewColor : currentColor,
                          cans
                        );
                      }
                    }}
                  />
                </div>

                {/* Скорость */}
                <div className="control-group">
                  <RangeSlider
                    label={`${getTranslation(lang, 'settingSpeed')}`}
                    value={speedFactorVal}
                    min={0.1}
                    max={5.0}
                    step={0.1}
                    onStart={() => setIsAdjusting(true)}
                    onEnd={() => setIsAdjusting(false)}
                    onChange={(value) => {
                      setSpeedFactorVal(value);
                      setActualSpeedFactor(value);
                      // Direct save (per-preset)
                      const all = lsGet(LS.SLIDER_VALS, {});
                      all[activePreset] = { radius: actualRadius, density: actualDensity, scale: actualSpeed, speedFactor: value };
                      lsSet(LS.SLIDER_VALS, all);

                      setUserOverrides(prev => ({
                        ...prev,
                        [activePreset]: {
                          ...(prev[activePreset] || {}),
                          speedFactor: value
                        }
                      }));

                      if (window.updateSprayParams) {
                        window.updateSprayParams(
                          actualRadius,
                          actualDensity,
                          actualSpeed,
                          value,
                          activePreset,
                          presets[activePreset]?.enableDrips || false,
                          presets[activePreset]?.paintConsumption || 1,
                          presets[activePreset],
                          activeCanId,
                          isPreviewMode ? previewColor : currentColor,
                          cans
                        );
                      }
                    }}
                  />
                </div>

                {/* Flat mode toggle */}
                {activeCanId > 4 && (
                  <div className="control-group">
                    <Switcher
                      label="Flat"
                      checked={flatMode}
                      onChange={setFlatMode}
                    />
                  </div>
                )}

              </div> {/* Конец .controls-panel-inner */}
            </div>

            {/* Палочка-тоггл (Expand/Collapse) */}
            <PanelToggle
              isExpanded={isControlsPanelExpanded}
              className="panel-toggle-stick"
              onClick={() => {
                setIsControlsPanelExpanded(!isControlsPanelExpanded);
                if (!isControlsPanelExpanded) setIsControlsCanvasExpanded(false);
              }}
            />
          </div>

          {/* Управление холстом */}
          <div className="panel-with-toggle">
            <div
              id="controls-canvas"
              style={{
                height: isControlsCanvasExpanded ? 'auto' : (isOnlyZoomMode ? '54px' : '216px'),
                transition: 'height 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative'
              }}
            >

              {/* Миниатюра холста (скрываем в режиме "Только зум" и в 3D режиме) */}
              {!isOnlyZoomMode && !is3DMode && (
                <CanvasMinimap
                  lang={lang}
                  showGrid={showGrid}
                  zoom={zoom}
                  minimapWidth={352}
                  minimapHeight={140}
                  style={{
                    background: 'var(--surface-control)',
                    borderRadius: 'var(--radius-md)',
                    boxSizing: 'border-box'
                  }}
                  title={getTranslation(lang, 'minimapTitleHint')}
                />
              )}

              <div className="controls-canvas-inner">
                {/* Ползунок масштаба */}
                <div className="control-group">
                  <RangeSlider
                    label={getTranslation(lang, 'lblZoom')}
                    value={Math.round((is3DMode ? zoom3D : zoom) * 100)}
                    min={is3DMode ? 25 : 20}
                    max={is3DMode ? 100 : 300}
                    step={1}
                    suffix="%"
                    layout="horizontal"
                    onChange={(val) => is3DMode ? setZoom3D(val / 100) : setZoom(val / 100)}
                    onValueClick={handleZoomTextClick}
                    onValueMouseEnter={handleZoomTextMouseEnter}
                    onValueMouseLeave={handleZoomTextMouseLeave}
                    valueDisplay={hoverZoomText || Math.round((is3DMode ? zoom3D : zoom) * 100) + '%'}
                  />
                </div>

                {/* Остальные элементы скрываем если включен "Только зум" и панель свернута */}
                {(!isOnlyZoomMode || isControlsCanvasExpanded) && (
                  <>
                    {/* Кнопка сброса холста */}
                    <Button id="resetBtn" variant="primary" fullWidth onClick={() => handleResetRequest(false)}>
                      {getTranslation(lang, 'btnResetWall')}
                    </Button>


                    {/* Переключатель сетки (1м / 800px) */}
                    <div className="control-group">
                      <Switcher
                        label={getTranslation(lang, 'lblToggleGrid')}
                        checked={showGrid}
                        disabled={is3DMode}
                        onChange={setShowGrid}
                      />
                    </div>

                    {/* Переключатель "Только зум" */}
                    <div className="control-group">
                      <Switcher
                        label={getTranslation(lang, 'lblOnlyZoom')}
                        checked={isOnlyZoomMode}
                        onChange={(val) => {
                          setIsOnlyZoomMode(val);
                          lsSet(LS.ONLY_ZOOM, val);
                          if (val) setIsControlsCanvasExpanded(false);
                        }}
                      />
                    </div>


                    {/* Контрольный штрих (Ghost mode) */}
                    <div className="control-group">
                      <Switcher
                        label={getTranslation(lang, 'lblEnableGhost')}
                        checked={isGhostEnabled}
                        onChange={(val) => {
                          setIsGhostEnabled(val);
                          lsSet(LS.GHOST_ENABLED, val);
                        }}
                      />
                    </div>

                    {/* Полноэкранный режим */}
                    <div className="control-group">
                      <Switcher
                        label={getTranslation(lang, 'lblFullScreen')}
                        checked={isFullscreen}
                        onChange={setIsFullscreen}
                      />
                    </div>

                    {/* Removed 3D mode toggle for unified separation */}

                    {/* 3D Режим обзора (View Mode) — показываем только если 3D включено */}
                    {is3DMode && (
                      <div className="control-group">
                        <Switcher
                          label={lang === 'ru' ? 'Свободный обзор (3D)' : 'Free View (3D)'}
                          checked={isPanMode}
                          onChange={setIsPanMode}
                        />
                      </div>
                    )}

                    {/* Загрузка фонового изображения */}
                    {!is3DMode && (
                      <div className="control-group">
                        <label style={{ marginTop: 0 }}>
                          {getTranslation(lang, 'lblBgInput')}{' '}
                          <input
                            type="file"
                            id="bgImageInput"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files && e.target.files[0];
                              if (!file) return;

                              const url = URL.createObjectURL(file);
                              const canvas = document.getElementById('sprayCanvas');
                              if (!canvas) {
                                console.error('Canvas #sprayCanvas not found');
                                return;
                              }

                              const ctx = canvas.getContext('2d');
                              const img = new Image();
                              img.onload = () => {
                                // Draw the background image to fill the canvas
                                // We use destination-over to place it BEHIND existing paint if any
                                const originalComp = ctx.globalCompositeOperation;
                                ctx.globalCompositeOperation = 'destination-over';
                                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                                ctx.globalCompositeOperation = originalComp;

                                // Also update the minimap if it exists
                                if (window.updateMinimap) window.updateMinimap();

                                URL.revokeObjectURL(url);
                              };
                              img.onerror = () => {
                                console.error('Failed to load background image');
                                URL.revokeObjectURL(url);
                              };
                              img.src = url;

                              // Reset input to allow re-uploading same file
                              e.target.value = '';
                            }}
                          />
                        </label>
                      </div>
                    )}
                  </>
                )}
              </div> {/* Конец .controls-canvas-inner */}
            </div>

            {/* Палочка-тоггл (Expand/Collapse) */}
            <PanelToggle
              isExpanded={isControlsCanvasExpanded}
              className="panel-toggle-stick"
              onClick={() => {
                setIsControlsCanvasExpanded(!isControlsCanvasExpanded);
                if (!isControlsCanvasExpanded) setIsControlsPanelExpanded(false);
              }}
            />
          </div>
        </div>
      )}


      {/* Оверлей для закрытия меню/языка/темы/хоткеев */}
      {(showMobileTopMenu || showLangModal || showChangelogModal || showThemeModal || showHotkeysModal) && (
        <div
          className="changelog-overlay"
          onClick={() => {
            setShowMobileTopMenu(false);
            setShowLangModal(false);
            setShowChangelogModal(false);
            setShowThemeModal(false);
            setShowHotkeysModal(false);
          }}
        />
      )}

      {/* Мобильное выпадающее меню */}
      {showMobileTopMenu && !showLangModal && (
        <div className="mobile-dropdown-modal">
          {isFullscreen && (
            <button
              className="dropdown-option"
              style={{ color: 'var(--color-brand)', fontWeight: 'bold' }}
              onClick={() => {
                setIsFullscreen(false);
                setShowMobileTopMenu(false);
              }}
            >
              {getTranslation(lang, 'fullScreenOff')}
            </button>
          )}

          <button
            className="dropdown-option"
            onClick={() => {
              setShowMobileTopMenu(false);
              setShowClearConfirm(true);
            }}
          >
            {getTranslation(lang, 'btnClearCanvas')}
          </button>

          <div className="dropdown-option" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{getTranslation(lang, 'lblToggleGrid')}</span>
            <Switcher
              checked={showGrid}
              onChange={(val) => {
                setShowGrid(val);
              }}
            />
          </div>

          <div className="dropdown-option" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{getTranslation(lang, 'lblEnableGhost')}</span>
            <Switcher
              checked={isGhostEnabled}
              onChange={(val) => {
                setIsGhostEnabled(val);
                lsSet(LS.GHOST_ENABLED, val);
              }}
            />
          </div>

          <button
            className="dropdown-option"
            onClick={() => {
              setShowMobileTopMenu(false);
              setShowLangModal(true);
            }}
          >
            {getTranslation(lang, 'lblLanguagePrefix')}
            {{ en: 'english', ru: 'cyrillic', es: 'spanish', pt: 'portuguese', de: 'deutsch', fr: 'français', th: 'ภาษาไทย', ko: '한국어', zh: '中文', ja: '日本語', hi: 'हिन्दी' }[lang] || lang}
          </button>

          <div className="dropdown-divider"></div>

          <a
            href="https://t.me/streetwallart"
            target="_blank"
            rel="noopener noreferrer"
            className="dropdown-option"
            onClick={() => setShowMobileTopMenu(false)}
          >
            feedback
          </a>

          <div className="dropdown-divider"></div>

          <div
            className="dropdown-option"
            onClick={() => {
              setShowMobileTopMenu(false);
              setShowChangelogModal(true);
            }}
          >
            v{changelogData[0].version}
          </div>

          {viewsCount !== null && (
            <div className="dropdown-option" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'default' }}>
              <span className="view-icon" style={{ fontSize: '1.3rem' }}>⚇</span> {viewsCount}
            </div>
          )}
        </div>
      )}

      {/* Версия приложения и Feedback (Desktop Footer) */}
      {!isFullscreen && (
        <div className="footer-links" style={{ position: 'fixed', bottom: 'var(--space-4)', left: 'var(--space-4)', zIndex: 100, display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
          <Link href={`/${lang}`} className="feedback-link" title={getTranslation(lang, 'footerBackTitle')} style={{ fontSize: '1rem', textDecoration: 'none', lineHeight: 1 }}>
            ❮
          </Link>

          <div
            id="version"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const wasOpen = showChangelogModal;
              closeAllModals();
              if (!wasOpen) setShowChangelogModal(true);
            }}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            title={getTranslation(lang, 'footerVersionTitle')}
          >
            <span>
              {changelogData[0].version}
            </span>
            <span style={{ margin: '0 var(--space-1)' }}>©</span>
            <span>
              streetwall.art
            </span>
          </div>

          <a
            href="https://t.me/streetwallart"
            target="_blank"
            rel="noopener noreferrer"
            className="feedback-link"
          >
            {getTranslation(lang, 'footerFeedback')}
          </a>

          <div
            className="feedback-link"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const wasOpen = showHotkeysModal;
              closeAllModals();
              if (!wasOpen) setShowHotkeysModal(true);
            }}
            style={{ cursor: 'pointer' }}
            title={getTranslation(lang, 'footerHotkeysTitle')}
          >
            {getTranslation(lang, 'footerHotkeysTitle')}
          </div>

          <div
            className="feedback-link"
            onClick={() => setShowLangModal(!showLangModal)}
            title={getTranslation(lang, 'footerLanguageTitle')}
          >
            {getTranslation(lang, 'lpHeaderLanguage').toLowerCase()}
          </div>

          <div
            className="feedback-link"
            onClick={() => {
              const wasOpen = showThemeModal;
              closeAllModals();
              if (!wasOpen) setShowThemeModal(true);
            }}
            title={getTranslation(lang, 'footerThemeTitle')}
          >
            {getTranslation(lang, 'lpHeaderTheme').toLowerCase()}
          </div>

          {viewsCount !== null ? (
            <div className="view-counter" title={getTranslation(lang, 'footerViewsTitle')}>
              <span className="view-icon">⚇</span> {viewsCount}
            </div>
          ) : (
            <div className="view-counter" style={{ opacity: 0.2 }} title={getTranslation(lang, 'loading')}>
              <span className="view-icon">⚇</span> ...
            </div>
          )}
        </div>
      )}

      {/* Модальное окно Changelog */}
      {showChangelogModal && (
        <div className="changelog-modal">
          <div className="changelog-content">
            {changelogData.map((release, idx) => (
              <div key={idx} className="changelog-entry">
                <div className="changelog-entry-header">
                  <span>v{release.version}</span>
                  <span className="changelog-entry-date">{release.date}</span>
                </div>
                <ul>
                  {release.changes[lang]?.map((change, cIdx) => (
                    <li key={cIdx}>{change}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {showLangModal && (
        <div className="lang-modal">
          <div style={{ padding: 'var(--space-2) var(--space-4)', fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>
            {getTranslation(lang, 'lpHeaderLanguage')}
          </div>
          {currentLanguages.map((l) => (
            <button
              key={l}
              className={`lang-option ${lang === l ? 'active' : ''}`}
              onClick={() => {
                setShowLangModal(false);
                const newPath = pathname.replace(`/${lang}`, `/${l}`);
                router.push(newPath || `/${l}/spraytest`);
              }}
            >
              {langLabels[l] || l.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {showThemeModal && (
        <div className="lang-modal">
          <div style={{ padding: 'var(--space-2) var(--space-4)', fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>{getTranslation(lang, 'lpHeaderTheme')}</div>
          {[
            { code: 'system', label: getTranslation(lang, 'themeSystem') },
            { code: 'dark', label: getTranslation(lang, 'themeDark') },
            { code: 'light', label: getTranslation(lang, 'themeLight') }
          ].map((theme) => (
            <button
              key={theme.code}
              className={`lang-option ${themeMode === theme.code ? 'active' : ''}`}
              onClick={() => {
                changeTheme(theme.code);
                setShowThemeModal(false);
              }}
            >
              {theme.label}
              {theme.code !== 'system' && (
                <span className="hotkey-badge" style={{ marginLeft: 'var(--space-2)' }}>t</span>
              )}
            </button>
          ))}

          <div style={{ padding: 'var(--space-2) var(--space-4)', marginTop: 'var(--space-2)', borderTop: '1px solid var(--border-subtle)', fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px', paddingTop: '8px' }}>{getTranslation(lang, 'lpHeaderStyle')}</div>
          {[
            { code: 'tokyo', label: 'tokyo' }
          ].map((styleOpt) => (
            <button
              key={styleOpt.code}
              className={`lang-option ${styleMode === styleOpt.code ? 'active' : ''}`}
              onClick={() => {
                changeStyle(styleOpt.code);
                setShowThemeModal(false);
              }}
            >
              {styleOpt.label}
            </button>
          ))}
        </div>
      )}

      {showHotkeysModal && (
        <div className="lang-modal" style={{ minWidth: '300px' }}>
          <div style={{ padding: 'var(--space-2) var(--space-4)', fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>
            {getTranslation(lang, 'footerHotkeysTitle')}
          </div>
          <div style={{ padding: '0 var(--space-4) var(--space-4)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {[
                { key: 'Q', desc: getTranslation(lang, 'hotkeyPresetBig') },
                { key: 'W', desc: getTranslation(lang, 'hotkeyPresetMid') },
                { key: 'E', desc: getTranslation(lang, 'hotkeyPresetSmall') },
                { key: 'T', desc: getTranslation(lang, 'hotkeyTheme') },
                { key: 'L', desc: getTranslation(lang, 'hotkeyFlashlight') },
                { key: '1', desc: 'Zoom 100%' },
                { key: '2', desc: 'Zoom 58%' },
                { key: '3', desc: 'Zoom 25%' },
                { key: '/', desc: getTranslation(lang, 'hotkeyPanMode') },
                { key: '\\', desc: getTranslation(lang, 'hotkeyFullScreen') },
                { key: 'Space / Shift', desc: getTranslation(lang, 'hotkeyHoldPan') },
                { key: 'Ctrl + Z', desc: getTranslation(lang, 'hotkeyUndo') },
                { key: 'Ctrl + S', desc: getTranslation(lang, 'hotkeySave') }
              ].map((hk, i) => (
                <div key={i} className="hotkey-modal-row">
                  <span className="hotkey-key">
                    {hk.key}
                  </span>
                  <span className="hotkey-desc">{hk.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {emptyCanAlert.show && (
        <div
          className="color-picker-overlay"
          onClick={() => setEmptyCanAlert({ show: false, canId: null })}
          style={{
            backgroundColor: 'rgba(0,0,0,0.1)',
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            paddingBottom: 'var(--space-12)',
            zIndex: 10001
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--surface-card)',
              borderRadius: 'var(--radius-xl)',
              border: '1px solid var(--border-subtle)',
              boxShadow: 'var(--shadow-lg)',
              padding: 'var(--space-6)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--space-5)',
              width: '90%',
              maxWidth: '360px',
              animation: 'slideUp 0.3s ease-out',
              backdropFilter: 'var(--glass-blur)',
              WebkitBackdropFilter: 'var(--glass-blur)'
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 var(--space-2) 0', color: 'var(--text-main)', fontSize: 'var(--text-lg)', fontWeight: '600' }}>
                {getTranslation(lang, 'modalEmptyCanTitle')}{emptyCanAlert.canId}
              </h3>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--text-sm)', lineHeight: '1.5' }}>
                {getTranslation(lang, 'modalEmptyCanRepeat')}
              </p>
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-3)', width: '100%' }}>
              <Button
                variant="subtle"
                fullWidth
                onClick={() => handleRemoveCan(emptyCanAlert.canId)}
                style={{ color: 'var(--text-muted)' }}
              >
                {getTranslation(lang, 'btnRefillNo')}
              </Button>
              <Button
                variant="primary"
                fullWidth
                onClick={() => handleRefill(emptyCanAlert.canId)}
              >
                {getTranslation(lang, 'btnRefillYes')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Алерт подтверждения очистки (снизу) */}
      {showClearConfirm && (
        <div
          className="color-picker-overlay"
          onClick={() => setShowClearConfirm(false)}
          style={{
            backgroundColor: 'rgba(0,0,0,0.1)',
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            paddingBottom: 'var(--space-12)',
            zIndex: 10001
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--surface-card)',
              borderRadius: 'var(--radius-xl)',
              border: '1px solid var(--border-subtle)',
              boxShadow: 'var(--shadow-lg)',
              padding: 'var(--space-6)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--space-5)',
              width: '90%',
              maxWidth: '360px',
              animation: 'slideUp 0.3s ease-out',
              backdropFilter: 'var(--glass-blur)',
              WebkitBackdropFilter: 'var(--glass-blur)'
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <h4 style={{ margin: '0 0 var(--space-2)', fontSize: '16px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                {getTranslation(lang, 'modalClearTitle')}
              </h4>
              <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.4' }}>
                {getTranslation(lang, 'modalClearMessage')}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-3)', width: '100%' }}>
              <Button
                variant="primary"
                size="sm"
                fullWidth
                onClick={() => {
                  handleClear();
                  setShowClearConfirm(false);
                  if (clearConfirmTimerRef.current) {
                    clearTimeout(clearConfirmTimerRef.current);
                    clearConfirmTimerRef.current = null;
                  }
                }}
              >
                {getTranslation(lang, 'btnYesEnter')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                fullWidth
                onClick={() => {
                  setShowClearConfirm(false);
                  if (clearConfirmTimerRef.current) {
                    clearTimeout(clearConfirmTimerRef.current);
                    clearConfirmTimerRef.current = null;
                  }
                }}
              >
                {getTranslation(lang, 'btnNoEsc')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showColorPickerFor && (
        <div className="color-picker-overlay" onClick={cancelColorSelection}>
          <div className="color-picker-modal" onClick={e => { e.stopPropagation(); resetAutoHideTimer(); }}>
            {activeColorTab === 'montana' && colorViewMode === 'cans' && (
              <CanPickerCanvas />
            )}
            <div className="color-picker-header">
              <h4>
                {getTranslation(lang, 'tooltipCanText')} {showColorPickerFor}
              </h4>

              <div className="color-picker-tabs">
                <Tab
                  tabs={[
                    { id: 'montana', label: getTranslation(lang, 'pickerTabMarket') },
                    { id: 'custom', label: getTranslation(lang, 'pickerTabCustom') }
                  ]}
                  activeId={activeColorTab}
                  selectedId={activeColorTab === 'montana' && colorViewMode === 'grid' ? 'montana' : null}
                  onChange={(tabId) => {
                    setActiveColorTab(tabId);
                    if (tabId === 'montana') {
                      setColorViewMode('cans');
                    }
                  }}
                  onSelect={(selectedId) => {
                    if (activeColorTab === 'montana') {
                      setColorViewMode(selectedId === 'montana' ? 'grid' : 'cans');
                    }
                  }}
                  allowSelection={activeColorTab === 'montana'}
                />
              </div>

              <button className="color-picker-close" onClick={cancelColorSelection}>✕</button>
            </div>



            <div className="color-picker-content" id="color-picker-scroll-container">
              {activeColorTab === 'montana' && (
                <div className="market-tab-container">
                  {colorViewMode === 'grid' ? (
                    <>
                      <h3 className="market-section-title">Montana Cans</h3>
                      <div className="montana-gallery">
                        {montanaColors.map((colorObj, idx) => (
                          <ColorTile
                            key={idx}
                            color={colorObj.hex}
                            code={colorObj.name.split(' ')[0]}
                            name={colorObj.name.substring(colorObj.name.indexOf(' ') + 1)}
                            isSelected={tempColor.toLowerCase() === colorObj.hex.toLowerCase()}
                            onClick={() => {
                              setTempColor(colorObj.hex);
                              handleColorChange({ target: { value: colorObj.hex } });
                              pendingColorNameRef.current = colorObj.name; // pass name to preview mode
                              applySelectedColor(colorObj.hex);
                              cancelColorSelection();
                            }}
                          />
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="cans-fishscale-gallery">
                      {montanaColors.map((c, index) => {
                        const row = Math.floor(index / 5);
                        return (
                          <CanPickerItem 
                            key={c.hex} 
                            colorObj={c} 
                            isSelected={tempColor.toLowerCase() === c.hex.toLowerCase()} 
                            onClick={() => {
                              setTempColor(c.hex);
                              handleColorChange({ target: { value: c.hex } });
                              pendingColorNameRef.current = c.name;
                              applySelectedColor(c.hex);
                              cancelColorSelection();
                            }}
                            zIndex={1001 + index}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {activeColorTab === 'custom' && (
                <div className="custom-colors-list">
                  <div className="custom-rows-header">
                    <span>{getTranslation(lang, 'customColorHeaderColor')}</span>
                    <span>{getTranslation(lang, 'customColorHeaderHex')}</span>
                    <span>{getTranslation(lang, 'customColorHeaderName')}</span>
                    <span></span>
                  </div>

                  {(() => {
                    const targetPickerCan = cans.find(c => c.id === showColorPickerFor);
                    const isRollerPicker = targetPickerCan?.type === 'roller';
                    const targetType = targetPickerCan?.type || 'spray';

                    // Replace mode: clicked a can that already has color and is replaceable (100% or 0%)
                    const isReplacingFilled = targetPickerCan && targetPickerCan.color &&
                      (targetPickerCan.paintLeft === 10000 || targetPickerCan.paintLeft === 0);

                    const sameTypeCans = cans.filter(c => c.type === targetType);
                    const emptyCount = sameTypeCans.filter(c => !c.color).length;

                    let maxAddable;
                    if (isReplacingFilled) {
                      maxAddable = 1;
                    } else if (isRollerPicker) {
                      // Rollers: only current empty slots
                      maxAddable = emptyCount;
                    } else {
                      // Sprays: empty slots + remaining space up to 30
                      const currentTotal = cans.length;
                      const canCreateNew = 30 - currentTotal;
                      maxAddable = emptyCount + canCreateNew;
                    }

                    const visibleRows = customColorRows.slice(0, maxAddable);
                    const lastRow = visibleRows[visibleRows.length - 1];
                    const canAddMore = !isReplacingFilled &&
                      visibleRows.length < maxAddable &&
                      lastRow && /^#([0-9A-F]{3}){1,2}$/i.test(lastRow.hex);

                    return (
                      <>
                        {visibleRows.map((row, index) => {
                          const isValidHex = /^#([0-9A-F]{3}){1,2}$/i.test(row.hex);
                          const isRowNotEmpty = row.hex || row.name;

                          return (
                            <div
                              className="custom-color-row"
                              key={index}
                              style={{ gridTemplateColumns: !isRowNotEmpty ? '48px 110px 1fr' : '48px 110px .982fr 44px' }}
                            >
                              <div
                                className="custom-row-indicator"
                                style={{ backgroundColor: isValidHex ? row.hex : 'transparent' }}
                                onClick={(e) => {
                                  const input = e.currentTarget.parentNode.querySelector('input[type="color"]');
                                  if (input) input.click();
                                }}
                              >
                                {!isValidHex && <div className="custom-row-empty-dot" />}
                              </div>

                              <Input
                                type="color"
                                variant="ghost"
                                value={isValidHex ? row.hex : '#ff3366'}
                                onChange={(e) => updateCustomColorRow(index, 'hex', e.target.value)}
                                style={{ opacity: 0, position: 'absolute', width: 0, height: 0 }}
                              />

                              <Input
                                type="text"
                                placeholder="#HEXCODE"
                                value={row.hex}
                                onChange={(e) => {
                                  let val = e.target.value.toUpperCase();
                                  if (!val.startsWith('#')) {
                                    val = '#' + val.replace(/#/g, '');
                                  }
                                  val = '#' + val.substring(1).replace(/[^0-9A-F]/g, '');
                                  if (val === '#') val = '';
                                  updateCustomColorRow(index, 'hex', val);
                                }}
                                maxLength={7}
                                onFocus={(e) => {
                                  if (e.target.value === '') updateCustomColorRow(index, 'hex', '#');
                                }}
                                onBlur={(e) => {
                                  if (e.target.value === '#') updateCustomColorRow(index, 'hex', '');
                                }}
                              />

                              <Input
                                type="text"
                                placeholder={getTranslation(lang, 'customColorPlaceholderName')}
                                value={row.name}
                                onChange={(e) => updateCustomColorRow(index, 'name', e.target.value)}
                                maxLength={44}
                              />

                              {isRowNotEmpty && (
                                <Button
                                  variant="outline"
                                  shape="square"
                                  onClick={() => clearCustomColorRow(index)}
                                  title={getTranslation(lang, 'customColorClearRowTitle')}
                                  style={{ borderRadius: 'var(--radius-sm)' }}
                                >
                                  ✕
                                </Button>
                              )}
                            </div>
                          );
                        })}

                        {canAddMore && (
                          <Button variant="ghost" fullWidth onClick={addMoreCustomRows} style={{ marginTop: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                            {getTranslation(lang, 'customColorAddMore')}
                          </Button>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            {activeColorTab === 'custom' && (
              <div className="color-picker-footer" style={{ display: 'flex', gap: 'var(--space-2)', padding: 'var(--space-4)', borderTop: '1px solid var(--border-subtle)' }}>
                <Button variant="primary" fullWidth onClick={() => {
                  const targetPickerCan = cans.find(c => c.id === showColorPickerFor);
                  const isRollerPicker = targetPickerCan?.type === 'roller';
                  const targetType = targetPickerCan?.type || 'spray';

                  const isReplacingFilled = targetPickerCan && targetPickerCan.color &&
                    (targetPickerCan.paintLeft === 10000 || targetPickerCan.paintLeft === 0);

                  const sameTypeCans = cans.filter(c => c.type === targetType);
                  const emptyCount = sameTypeCans.filter(c => !c.color).length;

                  let maxAddable;
                  if (isReplacingFilled) {
                    maxAddable = 1;
                  } else if (isRollerPicker) {
                    maxAddable = emptyCount;
                  } else {
                    const currentTotal = cans.length;
                    const canCreateNew = 30 - currentTotal;
                    maxAddable = emptyCount + canCreateNew;
                  }

                  const relevantRows = customColorRows.slice(0, maxAddable);
                  const validRows = relevantRows.filter(r => /^#([0-9A-F]{3}){1,2}$/i.test(r.hex));
                  if (validRows.length > 0) {
                    applyCustomColorBatch(validRows);
                  } else {
                    cancelColorSelection();
                  }
                }}>
                  {getTranslation(lang, 'btnApply')}
                </Button>
                <Button variant="outline" fullWidth onClick={cancelColorSelection}>
                  {getTranslation(lang, 'btnCancel')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Мобильный Bottom Toolbar - REMOVED per request */}

      {}


      {/* Контрольный штрих (Ghost Toast) */}
      {isAutoGhost && (
        <div className="ghost-toast-container" style={{
          position: 'fixed',
          bottom: '24px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2000,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none'
        }}>
          <div className="ghost-toast" style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-2) var(--space-4)',
            background: 'var(--surface-panel)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-full)',
            boxShadow: 'var(--shadow-lg)',
            pointerEvents: 'auto',
            animation: 'ghostToastFadeIn 0.3s ease-out'
          }}>
            <ColorIndicator
              color={previewColor || currentColor || '#fff'}
              percent={100}
              showPercent={false}
              shape="circle"
              size="sm"
            />
            <span style={{
              fontSize: 'var(--text-md)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--text-primary)',
              whiteSpace: 'nowrap'
            }}>
              {getTranslation(lang, 'autoGhostLabel')}
            </span>
            <Dropdown
              align="right"
              direction="up"
              trigger={(
                <button style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontSize: '20px',
                  padding: '0 4px',
                  display: 'flex',
                  alignItems: 'center'
                }}>⋮</button>
              )}
              options={[
                {
                  label: getTranslation(lang, 'btnDisableGhost'),
                  onClick: () => {
                    setIsGhostEnabled(false);
                    lsSet(LS.GHOST_ENABLED, false);
                  }
                }
              ]}
            />
          </div>
        </div>
      )}

      {/* Шторка управления холстом */}
      {showMobileCanvasSheet && (
        <>
          <div className="sheet-overlay" onClick={() => setShowMobileCanvasSheet(false)} />
          <div
            className="mobile-sheet canvas-sheet"
            onPointerDown={e => e.stopPropagation()}
            onTouchStart={e => e.stopPropagation()}
            onWheel={e => e.stopPropagation()}
          >
            <div className="sheet-header">
              <h3>{getTranslation(lang, '')}</h3>
              <button className="sheet-close" onClick={() => setShowMobileCanvasSheet(false)}>✕</button>
            </div>
            <div className="sheet-content">
              <div id="controls-canvas-mobile" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {/* Интерактивная миникарта */}
                <CanvasMinimap
                  lang={lang}
                  showGrid={showGrid}
                  zoom={zoom}
                  title={getTranslation(lang, 'minimapTitleHint')}
                />

                {/* Ползунок масштаба для мобильной шторки */}
                <div className="control-group">
                  <RangeSlider
                    label={getTranslation(lang, 'lblZoom')}
                    value={Math.round(zoom * 100)}
                    min={20}
                    max={300}
                    step={1}
                    suffix="%"
                    onChange={(value) => setZoom(value / 100)}
                  />
                </div>

                {/* Переключатель сетки (1м / 800px) для мобильной шторки */}
                <div className="control-group">
                  <Switcher
                    label={getTranslation(lang, 'lblToggleGrid')}
                    checked={showGrid}
                    onChange={setShowGrid}
                  />
                </div>

                {/* Кнопка сброса */}
                <Button
                  id="resetBtnMobile"
                  variant="primary"
                  fullWidth
                  onClick={() => {
                    const resetBtn = document.getElementById('resetBtn');
                    if (resetBtn) resetBtn.click();
                    setShowMobileCanvasSheet(false);
                  }}
                  style={{ marginTop: '10px', marginBottom: '12px' }}
                >
                  {getTranslation(lang, 'btnResetWall')}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {showMobileToolSheet && (
        <>
          <div className="sheet-overlay" onClick={() => setShowMobileToolSheet(false)} />
          <div className="mobile-sheet color-sheet" onClick={resetAutoHideTimer}>
            <div className="sheet-header" style={{ padding: 'var(--space-4) var(--space-4) var(--space-2)', gap: 'var(--space-2)', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <Tab
                  tabs={[
                    { id: 'spray', label: getTranslation(lang, 'tooltipCanText') },
                    { id: 'roller', label: getTranslation(lang, 'tabRoller') }
                  ]}
                  activeId={activeSlideType}
                  onChange={(tabId) => setActiveSlideType(tabId)}
                  fullWidth
                />
              </div>
              <Button
                variant="ghost"
                onClick={() => setShowMobileToolSheet(false)}
                style={{ height: '44px', width: '44px', minWidth: '44px', padding: 0, borderRadius: 'var(--radius-lg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}
              >
                ✕
              </Button>
            </div>
            <div className="sheet-content">
              <InfiniteCanSwiper
                cans={cans}
                activeCanId={activeCanId}
                selectCan={(id) => {
                  selectCan(id);
                  const can = cans.find(c => c.id === id);
                  const isReplaceable = !can?.color || can.paintLeft === 0 || can.paintLeft === 10000;
                  if (!isReplaceable) setShowMobileToolSheet(false);
                }}
                addNewCan={() => { addNewCan(); setShowMobileToolSheet(false); }}
                type={activeSlideType}
                setType={setActiveSlideType}
                isLarge={false}
                lang={lang}
                showDots={false}
              />
            </div>
          </div>
        </>
      )}

      {showMobileSettingsModal && (
        <>
          <div className="sheet-overlay" onClick={() => setShowMobileSettingsModal(false)} />
          <div className="mobile-sheet settings-modal" onClick={resetAutoHideTimer}>
            <div className="sheet-header" style={{ padding: 'var(--space-4) var(--space-4) var(--space-2)', gap: 'var(--space-2)', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <Tab
                  tabs={['Big', 'Mid', 'Small'].map(key => ({
                    id: key,
                    label: (
                      <>
                        {activePreset === key && flatMode ? <span className="flat-suffix">✹&nbsp;</span> : null}
                        {getTranslation(lang, `presetName${key}`)}
                      </>
                    )
                  }))}
                  activeId={activePreset}
                  selectedId={flatMode ? activePreset : null}
                  onChange={(tabId) => applyPreset(tabId)}
                  onSelect={(tabId) => setFlatMode(tabId !== null)}
                  allowSelection={true}
                  fullWidth
                />
              </div>
              <Button
                variant="ghost"
                onClick={() => setShowMobileSettingsModal(false)}
                style={{ height: '44px', width: '44px', minWidth: '44px', padding: 0, borderRadius: 'var(--radius-lg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}
              >
                ✕
              </Button>
            </div>
            <div className="sheet-content">

              {/* Кнопка сброса — только если настройки изменены, над слайдерами */}
              {presetManager.hasPresetChanges && (
                <div className="reset-settings-wrapper">
                  <button className="reset-settings-btn" onClick={handleResetPreset}>
                    {getTranslation(lang, 'btnResetPreset')}
                  </button>
                </div>
              )}

              {/* Scale и Radius — только для спрея (не для валика) */}
              {cans.find(c => c.id === activeCanId)?.type !== 'roller' && (
                <>
                  <div className="settings-group">
                    <RangeSlider
                      label="Scale"
                      value={scaleVal}
                      min={0.1}
                      max={2}
                      step={0.01}
                      onChange={(value) => {
                        setScaleVal(value);
                        setActualSpeed(value);
                        setUserOverrides(prev => ({
                          ...prev,
                          [activePreset]: { ...(prev[activePreset] || {}), scale: value }
                        }));
                        if (window.updateSprayParams) {
                          window.updateSprayParams(actualRadius, actualDensity, value, actualSpeedFactor, activePreset,
                            presets[activePreset]?.enableDrips || false, presets[activePreset]?.paintConsumption || 1,
                            presets[activePreset], activeCanId, isPreviewMode ? previewColor : currentColor, cans);
                        }
                      }}
                    />
                  </div>
                  <div className="settings-group">
                    <RangeSlider
                      label="Radius"
                      value={radiusVal}
                      min={10}
                      max={100}
                      step={1}
                      onChange={(value) => {
                        setRadiusVal(value);
                        setActualRadius(value);
                        setUserOverrides(prev => ({
                          ...prev,
                          [activePreset]: { ...(prev[activePreset] || {}), radius: value }
                        }));
                        if (window.updateSprayParams) {
                          window.updateSprayParams(value, actualDensity, actualSpeed, actualSpeedFactor, activePreset,
                            presets[activePreset]?.enableDrips || false, presets[activePreset]?.paintConsumption || 1,
                            presets[activePreset], activeCanId, isPreviewMode ? previewColor : currentColor, cans);
                        }
                      }}
                    />
                  </div>
                </>
              )}

              {/* Density и Speed — для всех (и спрей и валик) */}
              <div className="settings-group">
                <RangeSlider
                  label="Density"
                  value={densityVal}
                  min={100}
                  max={10000}
                  step={100}
                  onChange={(value) => {
                    setDensityVal(value);
                    setActualDensity(value);
                    setUserOverrides(prev => ({
                      ...prev,
                      [activePreset]: { ...(prev[activePreset] || {}), density: value }
                    }));
                    if (window.updateSprayParams) {
                      window.updateSprayParams(actualRadius, value, actualSpeed, actualSpeedFactor, activePreset,
                        presets[activePreset]?.enableDrips || false, presets[activePreset]?.paintConsumption || 1,
                        presets[activePreset], activeCanId, isPreviewMode ? previewColor : currentColor, cans);
                    }
                  }}
                />
              </div>
              <div className="settings-group">
                <RangeSlider
                  label="Speed"
                  value={speedFactorVal}
                  min={0.1}
                  max={5.0}
                  step={0.1}
                  onChange={(value) => {
                    setSpeedFactorVal(value);
                    setActualSpeedFactor(value);
                    setUserOverrides(prev => ({
                      ...prev,
                      [activePreset]: { ...(prev[activePreset] || {}), speedFactor: value }
                    }));
                    if (window.updateSprayParams) {
                      window.updateSprayParams(actualRadius, actualDensity, actualSpeed, value, activePreset,
                        presets[activePreset]?.enableDrips || false, presets[activePreset]?.paintConsumption || 1,
                        presets[activePreset], activeCanId, isPreviewMode ? previewColor : currentColor, cans);
                    }
                  }}
                />
              </div>

              {activeCanId > 4 && (
                <div className="settings-group settings-group-horizontal">
                  <Switcher
                    label="Flat"
                    checked={flatMode}
                    onChange={setFlatMode}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}